import { getControlSetterButtonId, getControlId } from './chunk-NBWQXLRW.mjs';
import { styled, ignoreSsrWarning, themes, ThemeProvider, convert, useTheme, ensure } from '@storybook/theming';
import React17, { createContext, lazy, Fragment, useState, useCallback, useRef, useEffect, Component, cloneElement, useMemo, Suspense, useContext, Children } from 'react';
import { withReset, SyntaxHighlighter, FlexBar, codeCommon, Icons, IconButton, Form, components, IconButtonSkeleton, Zoom as Zoom$1, ActionBar, ResetWrapper, Code, nameSpaceClassNames, H3, H2, Loader, TabsState, ErrorFormatter, getStoryHref, WithTooltipPure } from '@storybook/components';
import { transparentize, darken, opacify, lighten, rgba } from 'polished';
import { global } from '@storybook/global';
import { Icon, Link } from '@storybook/components/experimental';
import pickBy from 'lodash/pickBy.js';
import { includeConditionalArg } from '@storybook/csf';
import { deprecate, once, logger } from '@storybook/client-logger';
import Markdown from 'markdown-to-jsx';
import memoize from 'memoizerific';
import uniq from 'lodash/uniq.js';
import cloneDeep from 'lodash/cloneDeep.js';
import { filterArgTypes, composeConfigs, Preview as Preview$1, DocsContext as DocsContext$1 } from '@storybook/preview-api';
import mapValues from 'lodash/mapValues.js';
import { STORY_ARGS_UPDATED, UPDATE_STORY_ARGS, RESET_STORY_ARGS, GLOBALS_UPDATED, NAVIGATE_URL } from '@storybook/core-events';
import dedent2 from 'ts-dedent';
import { SNIPPET_RENDERED, SourceType, str } from '@storybook/docs-tools';
import { stringify } from 'telejson';
import tocbot from 'tocbot';
import { Channel } from '@storybook/channels';

var Wrapper = styled.div(withReset, ({ theme }) => ({
  backgroundColor: theme.base === "light" ? "rgba(0,0,0,.01)" : "rgba(255,255,255,.01)",
  borderRadius: theme.appBorderRadius,
  border: `1px dashed ${theme.appBorderColor}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
  margin: "25px 0 40px",
  color: transparentize(0.3, theme.color.defaultText),
  fontSize: theme.typography.size.s2
}));
var EmptyBlock = (props) => /* @__PURE__ */ React17.createElement(Wrapper, { ...props, className: "docblock-emptyblock sb-unstyled" });

// src/components/Source.tsx
var StyledSyntaxHighlighter = styled(
  SyntaxHighlighter
)(({ theme }) => ({
  // DocBlocks-specific styling and overrides
  fontSize: `${theme.typography.size.s2 - 1}px`,
  lineHeight: "19px",
  margin: "25px 0 40px",
  borderRadius: theme.appBorderRadius,
  boxShadow: theme.base === "light" ? "rgba(0, 0, 0, 0.10) 0 1px 3px 0" : "rgba(0, 0, 0, 0.20) 0 2px 5px 0",
  "pre.prismjs": {
    padding: 20,
    background: "inherit"
  }
}));
var SourceSkeletonWrapper = styled.div(({ theme }) => ({
  background: theme.background.content,
  borderRadius: theme.appBorderRadius,
  border: `1px solid ${theme.appBorderColor}`,
  boxShadow: theme.base === "light" ? "rgba(0, 0, 0, 0.10) 0 1px 3px 0" : "rgba(0, 0, 0, 0.20) 0 2px 5px 0",
  margin: "25px 0 40px",
  padding: "20px 20px 20px 22px"
}));
var SourceSkeletonPlaceholder = styled.div(({ theme }) => ({
  animation: `${theme.animation.glow} 1.5s ease-in-out infinite`,
  background: theme.appBorderColor,
  height: 17,
  marginTop: 1,
  width: "60%",
  [`&:first-child${ignoreSsrWarning}`]: {
    margin: 0
  }
}));
var SourceSkeleton = () => /* @__PURE__ */ React17.createElement(SourceSkeletonWrapper, null, /* @__PURE__ */ React17.createElement(SourceSkeletonPlaceholder, null), /* @__PURE__ */ React17.createElement(SourceSkeletonPlaceholder, { style: { width: "80%" } }), /* @__PURE__ */ React17.createElement(SourceSkeletonPlaceholder, { style: { width: "30%" } }), /* @__PURE__ */ React17.createElement(SourceSkeletonPlaceholder, { style: { width: "80%" } }));
var Source = ({
  isLoading,
  error,
  language,
  code,
  dark,
  format: format2,
  ...rest
}) => {
  if (isLoading) {
    return /* @__PURE__ */ React17.createElement(SourceSkeleton, null);
  }
  if (error) {
    return /* @__PURE__ */ React17.createElement(EmptyBlock, null, error);
  }
  const syntaxHighlighter = /* @__PURE__ */ React17.createElement(
    StyledSyntaxHighlighter,
    {
      bordered: true,
      copyable: true,
      format: format2,
      language,
      className: "docblock-source sb-unstyled",
      ...rest
    },
    code
  );
  if (typeof dark === "undefined") {
    return syntaxHighlighter;
  }
  const overrideTheme = dark ? themes.dark : themes.light;
  return /* @__PURE__ */ React17.createElement(ThemeProvider, { theme: convert(overrideTheme) }, syntaxHighlighter);
};
Source.defaultProps = {
  format: false
};
var toGlobalSelector = (element) => `& :where(${element}:not(.sb-anchor, .sb-unstyled, .sb-unstyled ${element}))`;
var breakpoint = 600;
var Title = styled.h1(withReset, ({ theme }) => ({
  color: theme.color.defaultText,
  fontSize: theme.typography.size.m3,
  fontWeight: theme.typography.weight.bold,
  lineHeight: "32px",
  [`@media (min-width: ${breakpoint}px)`]: {
    fontSize: theme.typography.size.l1,
    lineHeight: "36px",
    marginBottom: "16px"
  }
}));
var Subtitle = styled.h2(withReset, ({ theme }) => ({
  fontWeight: theme.typography.weight.regular,
  fontSize: theme.typography.size.s3,
  lineHeight: "20px",
  borderBottom: "none",
  marginBottom: 15,
  [`@media (min-width: ${breakpoint}px)`]: {
    fontSize: theme.typography.size.m1,
    lineHeight: "28px",
    marginBottom: 24
  },
  color: transparentize(0.25, theme.color.defaultText)
}));
var DocsContent = styled.div(({ theme }) => {
  const reset = {
    fontFamily: theme.typography.fonts.base,
    fontSize: theme.typography.size.s3,
    margin: 0,
    WebkitFontSmoothing: "antialiased",
    MozOsxFontSmoothing: "grayscale",
    WebkitTapHighlightColor: "rgba(0, 0, 0, 0)",
    WebkitOverflowScrolling: "touch"
  };
  const headers = {
    margin: "20px 0 8px",
    padding: 0,
    cursor: "text",
    position: "relative",
    color: theme.color.defaultText,
    "&:first-of-type": {
      marginTop: 0,
      paddingTop: 0
    },
    "&:hover a.anchor": {
      textDecoration: "none"
    },
    "& code": {
      fontSize: "inherit"
    }
  };
  const code = {
    lineHeight: 1,
    margin: "0 2px",
    padding: "3px 5px",
    whiteSpace: "nowrap",
    borderRadius: 3,
    fontSize: theme.typography.size.s2 - 1,
    border: theme.base === "light" ? `1px solid ${theme.color.mediumlight}` : `1px solid ${theme.color.darker}`,
    color: theme.base === "light" ? transparentize(0.1, theme.color.defaultText) : transparentize(0.3, theme.color.defaultText),
    backgroundColor: theme.base === "light" ? theme.color.lighter : theme.color.border
  };
  return {
    maxWidth: 1e3,
    width: "100%",
    [toGlobalSelector("a")]: {
      ...reset,
      fontSize: "inherit",
      lineHeight: "24px",
      color: theme.color.secondary,
      textDecoration: "none",
      "&.absent": {
        color: "#cc0000"
      },
      "&.anchor": {
        display: "block",
        paddingLeft: 30,
        marginLeft: -30,
        cursor: "pointer",
        position: "absolute",
        top: 0,
        left: 0,
        bottom: 0
      }
    },
    [toGlobalSelector("blockquote")]: {
      ...reset,
      margin: "16px 0",
      borderLeft: `4px solid ${theme.color.medium}`,
      padding: "0 15px",
      color: theme.color.dark,
      "& > :first-of-type": {
        marginTop: 0
      },
      "& > :last-child": {
        marginBottom: 0
      }
    },
    [toGlobalSelector("div")]: reset,
    [toGlobalSelector("dl")]: {
      ...reset,
      margin: "16px 0",
      padding: 0,
      "& dt": {
        fontSize: "14px",
        fontWeight: "bold",
        fontStyle: "italic",
        padding: 0,
        margin: "16px 0 4px"
      },
      "& dt:first-of-type": {
        padding: 0
      },
      "& dt > :first-of-type": {
        marginTop: 0
      },
      "& dt > :last-child": {
        marginBottom: 0
      },
      "& dd": {
        margin: "0 0 16px",
        padding: "0 15px"
      },
      "& dd > :first-of-type": {
        marginTop: 0
      },
      "& dd > :last-child": {
        marginBottom: 0
      }
    },
    [toGlobalSelector("h1")]: {
      ...reset,
      ...headers,
      fontSize: `${theme.typography.size.l1}px`,
      fontWeight: theme.typography.weight.bold
    },
    [toGlobalSelector("h2")]: {
      ...reset,
      ...headers,
      fontSize: `${theme.typography.size.m2}px`,
      paddingBottom: 4,
      borderBottom: `1px solid ${theme.appBorderColor}`
    },
    [toGlobalSelector("h3")]: {
      ...reset,
      ...headers,
      fontSize: `${theme.typography.size.m1}px`,
      fontWeight: theme.typography.weight.bold
    },
    [toGlobalSelector("h4")]: {
      ...reset,
      ...headers,
      fontSize: `${theme.typography.size.s3}px`
    },
    [toGlobalSelector("h5")]: {
      ...reset,
      ...headers,
      fontSize: `${theme.typography.size.s2}px`
    },
    [toGlobalSelector("h6")]: {
      ...reset,
      ...headers,
      fontSize: `${theme.typography.size.s2}px`,
      color: theme.color.dark
    },
    [toGlobalSelector("hr")]: {
      border: "0 none",
      borderTop: `1px solid ${theme.appBorderColor}`,
      height: 4,
      padding: 0
    },
    [toGlobalSelector("img")]: {
      maxWidth: "100%"
    },
    [toGlobalSelector("li")]: {
      ...reset,
      fontSize: theme.typography.size.s2,
      color: theme.color.defaultText,
      lineHeight: "24px",
      "& + li": {
        marginTop: ".25em"
      },
      "& ul, & ol": {
        marginTop: ".25em",
        marginBottom: 0
      },
      "& code": code
    },
    [toGlobalSelector("ol")]: {
      ...reset,
      margin: "16px 0",
      paddingLeft: 30,
      "& :first-of-type": {
        marginTop: 0
      },
      "& :last-child": {
        marginBottom: 0
      }
    },
    [toGlobalSelector("p")]: {
      ...reset,
      margin: "16px 0",
      fontSize: theme.typography.size.s2,
      lineHeight: "24px",
      color: theme.color.defaultText,
      "& code": code
    },
    [toGlobalSelector("pre")]: {
      ...reset,
      // reset
      fontFamily: theme.typography.fonts.mono,
      WebkitFontSmoothing: "antialiased",
      MozOsxFontSmoothing: "grayscale",
      lineHeight: "18px",
      padding: "11px 1rem",
      whiteSpace: "pre-wrap",
      color: "inherit",
      borderRadius: 3,
      margin: "1rem 0",
      "&:not(.prismjs)": {
        background: "transparent",
        border: "none",
        borderRadius: 0,
        padding: 0,
        margin: 0
      },
      "& pre, &.prismjs": {
        padding: 15,
        margin: 0,
        whiteSpace: "pre-wrap",
        color: "inherit",
        fontSize: "13px",
        lineHeight: "19px",
        code: {
          color: "inherit",
          fontSize: "inherit"
        }
      },
      "& code": {
        whiteSpace: "pre"
      },
      "& code, & tt": {
        border: "none"
      }
    },
    [toGlobalSelector("span")]: {
      ...reset,
      "&.frame": {
        display: "block",
        overflow: "hidden",
        "& > span": {
          border: `1px solid ${theme.color.medium}`,
          display: "block",
          float: "left",
          overflow: "hidden",
          margin: "13px 0 0",
          padding: 7,
          width: "auto"
        },
        "& span img": {
          display: "block",
          float: "left"
        },
        "& span span": {
          clear: "both",
          color: theme.color.darkest,
          display: "block",
          padding: "5px 0 0"
        }
      },
      "&.align-center": {
        display: "block",
        overflow: "hidden",
        clear: "both",
        "& > span": {
          display: "block",
          overflow: "hidden",
          margin: "13px auto 0",
          textAlign: "center"
        },
        "& span img": {
          margin: "0 auto",
          textAlign: "center"
        }
      },
      "&.align-right": {
        display: "block",
        overflow: "hidden",
        clear: "both",
        "& > span": {
          display: "block",
          overflow: "hidden",
          margin: "13px 0 0",
          textAlign: "right"
        },
        "& span img": {
          margin: 0,
          textAlign: "right"
        }
      },
      "&.float-left": {
        display: "block",
        marginRight: 13,
        overflow: "hidden",
        float: "left",
        "& span": {
          margin: "13px 0 0"
        }
      },
      "&.float-right": {
        display: "block",
        marginLeft: 13,
        overflow: "hidden",
        float: "right",
        "& > span": {
          display: "block",
          overflow: "hidden",
          margin: "13px auto 0",
          textAlign: "right"
        }
      }
    },
    [toGlobalSelector("table")]: {
      ...reset,
      margin: "16px 0",
      fontSize: theme.typography.size.s2,
      lineHeight: "24px",
      padding: 0,
      borderCollapse: "collapse",
      "& tr": {
        borderTop: `1px solid ${theme.appBorderColor}`,
        backgroundColor: theme.appContentBg,
        margin: 0,
        padding: 0
      },
      "& tr:nth-of-type(2n)": {
        backgroundColor: theme.base === "dark" ? theme.color.darker : theme.color.lighter
      },
      "& tr th": {
        fontWeight: "bold",
        color: theme.color.defaultText,
        border: `1px solid ${theme.appBorderColor}`,
        margin: 0,
        padding: "6px 13px"
      },
      "& tr td": {
        border: `1px solid ${theme.appBorderColor}`,
        color: theme.color.defaultText,
        margin: 0,
        padding: "6px 13px"
      },
      "& tr th :first-of-type, & tr td :first-of-type": {
        marginTop: 0
      },
      "& tr th :last-child, & tr td :last-child": {
        marginBottom: 0
      }
    },
    [toGlobalSelector("ul")]: {
      ...reset,
      margin: "16px 0",
      paddingLeft: 30,
      "& :first-of-type": {
        marginTop: 0
      },
      "& :last-child": {
        marginBottom: 0
      },
      listStyle: "disc"
    }
  };
});
var DocsWrapper = styled.div(({ theme }) => ({
  background: theme.background.content,
  display: "flex",
  justifyContent: "center",
  padding: "4rem 20px",
  minHeight: "100vh",
  boxSizing: "border-box",
  gap: "3rem",
  [`@media (min-width: ${breakpoint}px)`]: {}
}));
var DocsPageWrapper = ({ children, toc }) => /* @__PURE__ */ React17.createElement(DocsWrapper, { className: "sbdocs sbdocs-wrapper" }, /* @__PURE__ */ React17.createElement(DocsContent, { className: "sbdocs sbdocs-content" }, children), toc);

// src/components/BlockBackgroundStyles.tsx
var getBlockBackgroundStyle = (theme) => ({
  borderRadius: theme.appBorderRadius,
  background: theme.background.content,
  boxShadow: theme.base === "light" ? "rgba(0, 0, 0, 0.10) 0 1px 3px 0" : "rgba(0, 0, 0, 0.20) 0 2px 5px 0",
  border: `1px solid ${theme.appBorderColor}`
});
var Zoom = ({ zoom, resetZoom }) => /* @__PURE__ */ React17.createElement(React17.Fragment, null, /* @__PURE__ */ React17.createElement(
  IconButton,
  {
    key: "zoomin",
    onClick: (e) => {
      e.preventDefault();
      zoom(0.8);
    },
    title: "Zoom in"
  },
  /* @__PURE__ */ React17.createElement(Icon.Zoom, null)
), /* @__PURE__ */ React17.createElement(
  IconButton,
  {
    key: "zoomout",
    onClick: (e) => {
      e.preventDefault();
      zoom(1.25);
    },
    title: "Zoom out"
  },
  /* @__PURE__ */ React17.createElement(Icon.ZoomOut, null)
), /* @__PURE__ */ React17.createElement(
  IconButton,
  {
    key: "zoomreset",
    onClick: (e) => {
      e.preventDefault();
      resetZoom();
    },
    title: "Reset zoom"
  },
  /* @__PURE__ */ React17.createElement(Icon.ZoomReset, null)
));
var Bar = styled(FlexBar)({
  position: "absolute",
  left: 0,
  right: 0,
  top: 0,
  transition: "transform .2s linear"
});
var Toolbar = ({
  isLoading,
  storyId,
  baseUrl,
  zoom,
  resetZoom,
  ...rest
}) => /* @__PURE__ */ React17.createElement(Bar, { ...rest }, /* @__PURE__ */ React17.createElement(Fragment, { key: "left" }, isLoading ? [1, 2, 3].map((key) => /* @__PURE__ */ React17.createElement(IconButtonSkeleton, { key })) : /* @__PURE__ */ React17.createElement(Zoom, { ...{ zoom, resetZoom } })));
var ZoomContext = createContext({
  scale: 1
});
var { window: globalWindow } = global;
var IFrame = class extends Component {
  constructor() {
    super(...arguments);
    this.iframe = null;
  }
  componentDidMount() {
    const { id } = this.props;
    this.iframe = globalWindow.document.getElementById(id);
  }
  shouldComponentUpdate(nextProps) {
    const { scale } = nextProps;
    if (scale !== this.props.scale) {
      this.setIframeBodyStyle({
        width: `${scale * 100}%`,
        height: `${scale * 100}%`,
        transform: `scale(${1 / scale})`,
        transformOrigin: "top left"
      });
    }
    return false;
  }
  setIframeBodyStyle(style) {
    return Object.assign(this.iframe.contentDocument.body.style, style);
  }
  render() {
    const { id, title, src, allowFullScreen, scale, ...rest } = this.props;
    return /* @__PURE__ */ React17.createElement(
      "iframe",
      {
        id,
        title,
        src,
        ...allowFullScreen ? { allow: "fullscreen" } : {},
        loading: "lazy",
        ...rest
      }
    );
  }
};

// src/components/Story.tsx
var { PREVIEW_URL } = global;
var BASE_URL = PREVIEW_URL || "iframe.html";
var storyBlockIdFromId = ({ story, primary }) => `story--${story.id}${primary ? "--primary" : ""}`;
var InlineStory = (props) => {
  const storyRef = useRef();
  const [showLoader, setShowLoader] = useState(true);
  const [error, setError] = useState();
  const { story, height, autoplay, forceInitialArgs, renderStoryToElement } = props;
  useEffect(() => {
    if (!(story && storyRef.current)) {
      return () => {
      };
    }
    const element = storyRef.current;
    const cleanup = renderStoryToElement(
      story,
      element,
      {
        showMain: () => {
        },
        showError: ({ title, description }) => setError(new Error(`${title} - ${description}`)),
        showException: (err) => setError(err)
      },
      { autoplay, forceInitialArgs }
    );
    setShowLoader(false);
    return () => {
      Promise.resolve().then(() => cleanup());
    };
  }, [autoplay, renderStoryToElement, story]);
  const htmlContents = `<span></span>`;
  if (error) {
    return /* @__PURE__ */ React17.createElement("pre", null, /* @__PURE__ */ React17.createElement(ErrorFormatter, { error }));
  }
  return /* @__PURE__ */ React17.createElement(React17.Fragment, null, height ? /* @__PURE__ */ React17.createElement("style", null, `#${storyBlockIdFromId(
    props
  )} { min-height: ${height}; transform: translateZ(0); overflow: auto }`) : null, showLoader && /* @__PURE__ */ React17.createElement(StorySkeleton, null), /* @__PURE__ */ React17.createElement(
    "div",
    {
      ref: storyRef,
      id: `${storyBlockIdFromId(props)}-inner`,
      "data-name": story.name,
      dangerouslySetInnerHTML: { __html: htmlContents }
    }
  ));
};
var IFrameStory = ({ story, height = "500px" }) => /* @__PURE__ */ React17.createElement("div", { style: { width: "100%", height } }, /* @__PURE__ */ React17.createElement(ZoomContext.Consumer, null, ({ scale }) => {
  return /* @__PURE__ */ React17.createElement(
    IFrame,
    {
      key: "iframe",
      id: `iframe--${story.id}`,
      title: story.name,
      src: getStoryHref(BASE_URL, story.id, { viewMode: "story" }),
      allowFullScreen: true,
      scale,
      style: {
        width: "100%",
        height: "100%",
        border: "0 none"
      }
    }
  );
}));
var Story = (props) => {
  const { inline } = props;
  return /* @__PURE__ */ React17.createElement("div", { id: storyBlockIdFromId(props), className: "sb-story sb-unstyled", "data-story-block": "true" }, inline ? /* @__PURE__ */ React17.createElement(InlineStory, { ...props }) : /* @__PURE__ */ React17.createElement(IFrameStory, { ...props }));
};
var StorySkeleton = () => /* @__PURE__ */ React17.createElement(Loader, null);

// src/components/Preview.tsx
var ChildrenContainer = styled.div(
  ({ isColumn, columns, layout }) => ({
    display: isColumn || !columns ? "block" : "flex",
    position: "relative",
    flexWrap: "wrap",
    overflow: "auto",
    flexDirection: isColumn ? "column" : "row",
    "& .innerZoomElementWrapper > *": isColumn ? {
      width: layout !== "fullscreen" ? "calc(100% - 20px)" : "100%",
      display: "block"
    } : {
      maxWidth: layout !== "fullscreen" ? "calc(100% - 20px)" : "100%",
      display: "inline-block"
    }
  }),
  ({ layout = "padded" }) => layout === "centered" || layout === "padded" ? {
    padding: "30px 20px",
    margin: -10,
    "& .innerZoomElementWrapper > *": {
      width: "auto",
      border: "10px solid transparent!important"
    }
  } : {},
  ({ layout = "padded" }) => layout === "centered" ? {
    display: "flex",
    justifyContent: "center",
    justifyItems: "center",
    alignContent: "center",
    alignItems: "center"
  } : {},
  ({ columns }) => columns && columns > 1 ? { ".innerZoomElementWrapper > *": { minWidth: `calc(100% / ${columns} - 20px)` } } : {}
);
var StyledSource = styled(Source)(({ theme }) => ({
  margin: 0,
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0,
  borderBottomLeftRadius: theme.appBorderRadius,
  borderBottomRightRadius: theme.appBorderRadius,
  border: "none",
  background: theme.base === "light" ? "rgba(0, 0, 0, 0.85)" : darken(0.05, theme.background.content),
  color: theme.color.lightest,
  button: {
    background: theme.base === "light" ? "rgba(0, 0, 0, 0.85)" : darken(0.05, theme.background.content)
  }
}));
var PreviewContainer = styled.div(
  ({ theme, withSource, isExpanded }) => ({
    position: "relative",
    overflow: "hidden",
    margin: "25px 0 40px",
    ...getBlockBackgroundStyle(theme),
    borderBottomLeftRadius: withSource && isExpanded && 0,
    borderBottomRightRadius: withSource && isExpanded && 0,
    borderBottomWidth: isExpanded && 0,
    "h3 + &": {
      marginTop: "16px"
    }
  }),
  ({ withToolbar }) => withToolbar && { paddingTop: 40 }
);
var getSource = (withSource, expanded, setExpanded) => {
  switch (true) {
    case !!(withSource && withSource.error): {
      return {
        source: null,
        actionItem: {
          title: "No code available",
          className: "docblock-code-toggle docblock-code-toggle--disabled",
          disabled: true,
          onClick: () => setExpanded(false)
        }
      };
    }
    case expanded: {
      return {
        source: /* @__PURE__ */ React17.createElement(StyledSource, { ...withSource, dark: true }),
        actionItem: {
          title: "Hide code",
          className: "docblock-code-toggle docblock-code-toggle--expanded",
          onClick: () => setExpanded(false)
        }
      };
    }
    default: {
      return {
        source: /* @__PURE__ */ React17.createElement(StyledSource, { ...withSource, dark: true }),
        actionItem: {
          title: "Show code",
          className: "docblock-code-toggle",
          onClick: () => setExpanded(true)
        }
      };
    }
  }
};
function getStoryId(children) {
  if (Children.count(children) === 1) {
    const elt = children;
    if (elt.props) {
      return elt.props.id;
    }
  }
  return null;
}
var PositionedToolbar = styled(Toolbar)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  height: 40
});
var Relative = styled.div({
  overflow: "hidden",
  position: "relative"
});
var Preview = ({
  isLoading,
  isColumn,
  columns,
  children,
  withSource,
  withToolbar = false,
  isExpanded = false,
  additionalActions,
  className,
  layout = "padded",
  ...props
}) => {
  const [expanded, setExpanded] = useState(isExpanded);
  const { source, actionItem } = getSource(withSource, expanded, setExpanded);
  const [scale, setScale] = useState(1);
  const previewClasses = [className].concat(["sbdocs", "sbdocs-preview", "sb-unstyled"]);
  const defaultActionItems = withSource ? [actionItem] : [];
  const [additionalActionItems, setAdditionalActionItems] = useState(
    additionalActions ? [...additionalActions] : []
  );
  const actionItems = [...defaultActionItems, ...additionalActionItems];
  const { window: globalWindow4 } = global;
  const copyToClipboard = useCallback(async (text) => {
    const { createCopyToClipboardFunction } = await import('@storybook/components');
    createCopyToClipboardFunction();
  }, []);
  const onCopyCapture = (e) => {
    const selection = globalWindow4.getSelection();
    if (selection && selection.type === "Range") {
      return;
    }
    e.preventDefault();
    if (additionalActionItems.filter((item) => item.title === "Copied").length === 0) {
      copyToClipboard(source.props.code).then(() => {
        setAdditionalActionItems([
          ...additionalActionItems,
          {
            title: "Copied",
            onClick: () => {
            }
          }
        ]);
        globalWindow4.setTimeout(
          () => setAdditionalActionItems(
            additionalActionItems.filter((item) => item.title !== "Copied")
          ),
          1500
        );
      });
    }
  };
  return /* @__PURE__ */ React17.createElement(
    PreviewContainer,
    {
      ...{ withSource, withToolbar },
      ...props,
      className: previewClasses.join(" ")
    },
    withToolbar && /* @__PURE__ */ React17.createElement(
      PositionedToolbar,
      {
        isLoading,
        border: true,
        zoom: (z) => setScale(scale * z),
        resetZoom: () => setScale(1),
        storyId: getStoryId(children),
        baseUrl: "./iframe.html"
      }
    ),
    /* @__PURE__ */ React17.createElement(ZoomContext.Provider, { value: { scale } }, /* @__PURE__ */ React17.createElement(Relative, { className: "docs-story", onCopyCapture: withSource && onCopyCapture }, /* @__PURE__ */ React17.createElement(
      ChildrenContainer,
      {
        isColumn: isColumn || !Array.isArray(children),
        columns,
        layout
      },
      /* @__PURE__ */ React17.createElement(Zoom$1.Element, { scale }, Array.isArray(children) ? (
        // eslint-disable-next-line react/no-array-index-key
        children.map((child, i) => /* @__PURE__ */ React17.createElement("div", { key: i }, child))
      ) : /* @__PURE__ */ React17.createElement("div", null, children))
    ), /* @__PURE__ */ React17.createElement(ActionBar, { actionItems }))),
    withSource && expanded && source
  );
};
var StyledPreview = styled(Preview)(() => ({
  ".docs-story": {
    paddingTop: 32,
    paddingBottom: 40
  }
}));
var PreviewSkeleton = () => /* @__PURE__ */ React17.createElement(StyledPreview, { isLoading: true, withToolbar: true }, /* @__PURE__ */ React17.createElement(StorySkeleton, null));
var Table = styled.table(({ theme }) => ({
  "&&": {
    // Escape default table styles
    borderCollapse: "collapse",
    borderSpacing: 0,
    border: "none",
    tr: {
      border: "none !important",
      background: "none"
    },
    "td, th": {
      padding: 0,
      border: "none",
      width: "auto!important"
    },
    // End escape
    marginTop: 0,
    marginBottom: 0,
    "th:first-of-type, td:first-of-type": {
      paddingLeft: 0
    },
    "th:last-of-type, td:last-of-type": {
      paddingRight: 0
    },
    td: {
      paddingTop: 0,
      paddingBottom: 4,
      "&:not(:first-of-type)": {
        paddingLeft: 10,
        paddingRight: 0
      }
    },
    tbody: {
      boxShadow: "none",
      border: "none"
    },
    code: codeCommon({ theme }),
    div: {
      span: {
        fontWeight: "bold"
      }
    },
    "& code": {
      margin: 0,
      display: "inline-block",
      fontSize: theme.typography.size.s1
    }
  }
}));
var ArgJsDoc = ({ tags }) => {
  const params = (tags.params || []).filter((x) => x.description);
  const hasDisplayableParams = params.length !== 0;
  const hasDisplayableDeprecated = tags.deprecated != null;
  const hasDisplayableReturns = tags.returns != null && tags.returns.description != null;
  if (!hasDisplayableParams && !hasDisplayableReturns && !hasDisplayableDeprecated) {
    return null;
  }
  return /* @__PURE__ */ React17.createElement(React17.Fragment, null, /* @__PURE__ */ React17.createElement(Table, null, /* @__PURE__ */ React17.createElement("tbody", null, hasDisplayableDeprecated && /* @__PURE__ */ React17.createElement("tr", { key: "deprecated" }, /* @__PURE__ */ React17.createElement("td", { colSpan: 2 }, /* @__PURE__ */ React17.createElement("strong", null, "Deprecated"), ": ", tags.deprecated)), hasDisplayableParams && params.map((x) => {
    return /* @__PURE__ */ React17.createElement("tr", { key: x.name }, /* @__PURE__ */ React17.createElement("td", null, /* @__PURE__ */ React17.createElement("code", null, x.name)), /* @__PURE__ */ React17.createElement("td", null, x.description));
  }), hasDisplayableReturns && /* @__PURE__ */ React17.createElement("tr", { key: "returns" }, /* @__PURE__ */ React17.createElement("td", null, /* @__PURE__ */ React17.createElement("code", null, "Returns")), /* @__PURE__ */ React17.createElement("td", null, tags.returns.description)))));
};
var ITEMS_BEFORE_EXPANSION = 8;
var Summary = styled.div(({ isExpanded }) => ({
  display: "flex",
  flexDirection: isExpanded ? "column" : "row",
  flexWrap: "wrap",
  alignItems: "flex-start",
  marginBottom: "-4px",
  minWidth: 100
}));
var Text = styled.span(codeCommon, ({ theme, simple = false }) => ({
  flex: "0 0 auto",
  fontFamily: theme.typography.fonts.mono,
  fontSize: theme.typography.size.s1,
  wordBreak: "break-word",
  whiteSpace: "normal",
  maxWidth: "100%",
  margin: 0,
  marginRight: "4px",
  marginBottom: "4px",
  paddingTop: "2px",
  paddingBottom: "2px",
  lineHeight: "13px",
  ...simple && {
    background: "transparent",
    border: "0 none",
    paddingLeft: 0
  }
}));
var ExpandButton = styled.button(({ theme }) => ({
  fontFamily: theme.typography.fonts.mono,
  color: theme.color.secondary,
  marginBottom: "4px",
  background: "none",
  border: "none"
}));
var Expandable = styled.div(codeCommon, ({ theme }) => ({
  fontFamily: theme.typography.fonts.mono,
  color: theme.color.secondary,
  fontSize: theme.typography.size.s1,
  // overrides codeCommon
  margin: 0,
  whiteSpace: "nowrap",
  display: "flex",
  alignItems: "center"
}));
var Detail = styled.div(({ theme, width }) => ({
  width,
  minWidth: 200,
  maxWidth: 800,
  padding: 15,
  // Don't remove the mono fontFamily here even if it seems useless, this is used by the browser to calculate the length of a "ch" unit.
  fontFamily: theme.typography.fonts.mono,
  fontSize: theme.typography.size.s1,
  // Most custom stylesheet will reset the box-sizing to "border-box" and will break the tooltip.
  boxSizing: "content-box",
  "& code": {
    padding: "0 !important"
  }
}));
var ArrowIcon = styled(Icons)({
  height: 10,
  width: 10,
  minWidth: 10,
  marginLeft: 4
});
var EmptyArg = () => {
  return /* @__PURE__ */ React17.createElement("span", null, "-");
};
var ArgText = ({ text, simple }) => {
  return /* @__PURE__ */ React17.createElement(Text, { simple }, text);
};
var calculateDetailWidth = memoize(1e3)((detail) => {
  const lines = detail.split(/\r?\n/);
  return `${Math.max(...lines.map((x) => x.length))}ch`;
});
var getSummaryItems = (summary) => {
  if (!summary)
    return [summary];
  const splittedItems = summary.split("|");
  const summaryItems = splittedItems.map((value2) => value2.trim());
  return uniq(summaryItems);
};
var renderSummaryItems = (summaryItems, isExpanded = true) => {
  let items = summaryItems;
  if (!isExpanded) {
    items = summaryItems.slice(0, ITEMS_BEFORE_EXPANSION);
  }
  return items.map((item) => /* @__PURE__ */ React17.createElement(ArgText, { key: item, text: item === "" ? '""' : item }));
};
var ArgSummary = ({ value: value2, initialExpandedArgs }) => {
  const { summary, detail } = value2;
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(initialExpandedArgs || false);
  if (summary === void 0 || summary === null)
    return null;
  const summaryAsString = typeof summary.toString === "function" ? summary.toString() : summary;
  if (detail == null) {
    const cannotBeSafelySplitted = /[(){}[\]<>]/.test(summaryAsString);
    if (cannotBeSafelySplitted) {
      return /* @__PURE__ */ React17.createElement(ArgText, { text: summaryAsString });
    }
    const summaryItems = getSummaryItems(summaryAsString);
    const itemsCount = summaryItems.length;
    const hasManyItems = itemsCount > ITEMS_BEFORE_EXPANSION;
    return hasManyItems ? /* @__PURE__ */ React17.createElement(Summary, { isExpanded }, renderSummaryItems(summaryItems, isExpanded), /* @__PURE__ */ React17.createElement(ExpandButton, { onClick: () => setIsExpanded(!isExpanded) }, isExpanded ? "Show less..." : `Show ${itemsCount - ITEMS_BEFORE_EXPANSION} more...`)) : /* @__PURE__ */ React17.createElement(Summary, null, renderSummaryItems(summaryItems));
  }
  return /* @__PURE__ */ React17.createElement(
    WithTooltipPure,
    {
      closeOnOutsideClick: true,
      placement: "bottom",
      visible: isOpen,
      onVisibleChange: (isVisible) => {
        setIsOpen(isVisible);
      },
      tooltip: /* @__PURE__ */ React17.createElement(Detail, { width: calculateDetailWidth(detail) }, /* @__PURE__ */ React17.createElement(SyntaxHighlighter, { language: "jsx", format: false }, detail))
    },
    /* @__PURE__ */ React17.createElement(Expandable, { className: "sbdocs-expandable" }, /* @__PURE__ */ React17.createElement("span", null, summaryAsString), /* @__PURE__ */ React17.createElement(ArrowIcon, { icon: isOpen ? "arrowup" : "arrowdown" }))
  );
};
var ArgValue = ({ value: value2, initialExpandedArgs }) => {
  return value2 == null ? /* @__PURE__ */ React17.createElement(EmptyArg, null) : /* @__PURE__ */ React17.createElement(ArgSummary, { value: value2, initialExpandedArgs });
};
var Label = styled.label(({ theme }) => ({
  lineHeight: "18px",
  alignItems: "center",
  marginBottom: 8,
  display: "inline-block",
  position: "relative",
  whiteSpace: "nowrap",
  background: theme.boolean.background,
  borderRadius: "3em",
  padding: 1,
  input: {
    appearance: "none",
    width: "100%",
    height: "100%",
    position: "absolute",
    left: 0,
    top: 0,
    margin: 0,
    padding: 0,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    borderRadius: "3em",
    "&:focus": {
      outline: "none",
      boxShadow: `${theme.color.secondary} 0 0 0 1px inset !important`
    }
  },
  span: {
    textAlign: "center",
    fontSize: theme.typography.size.s1,
    fontWeight: theme.typography.weight.bold,
    lineHeight: "1",
    cursor: "pointer",
    display: "inline-block",
    padding: "7px 15px",
    transition: "all 100ms ease-out",
    userSelect: "none",
    borderRadius: "3em",
    color: transparentize(0.5, theme.color.defaultText),
    background: "transparent",
    "&:hover": {
      boxShadow: `${opacify(0.3, theme.appBorderColor)} 0 0 0 1px inset`
    },
    "&:active": {
      boxShadow: `${opacify(0.05, theme.appBorderColor)} 0 0 0 2px inset`,
      color: opacify(1, theme.appBorderColor)
    },
    "&:first-of-type": {
      paddingRight: 8
    },
    "&:last-of-type": {
      paddingLeft: 8
    }
  },
  "input:checked ~ span:last-of-type, input:not(:checked) ~ span:first-of-type": {
    background: theme.boolean.selectedBackground,
    boxShadow: theme.base === "light" ? `${opacify(0.1, theme.appBorderColor)} 0 0 2px` : `${theme.appBorderColor} 0 0 0 1px`,
    color: theme.color.defaultText,
    padding: "7px 15px"
  }
}));
var parse = (value2) => value2 === "true";
var BooleanControl = ({ name, value: value2, onChange, onBlur, onFocus }) => {
  const onSetFalse = useCallback(() => onChange(false), [onChange]);
  if (value2 === void 0) {
    return /* @__PURE__ */ React17.createElement(Form.Button, { id: getControlSetterButtonId(name), onClick: onSetFalse }, "Set boolean");
  }
  const controlId = getControlId(name);
  const parsedValue = typeof value2 === "string" ? parse(value2) : value2;
  return /* @__PURE__ */ React17.createElement(Label, { htmlFor: controlId, title: parsedValue ? "Change to false" : "Change to true" }, /* @__PURE__ */ React17.createElement(
    "input",
    {
      id: controlId,
      type: "checkbox",
      onChange: (e) => onChange(e.target.checked),
      checked: parsedValue,
      ...{ name, onBlur, onFocus }
    }
  ), /* @__PURE__ */ React17.createElement("span", null, "False"), /* @__PURE__ */ React17.createElement("span", null, "True"));
};
var parseDate = (value2) => {
  const [year, month, day] = value2.split("-");
  const result = /* @__PURE__ */ new Date();
  result.setFullYear(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
  return result;
};
var parseTime = (value2) => {
  const [hours, minutes] = value2.split(":");
  const result = /* @__PURE__ */ new Date();
  result.setHours(parseInt(hours, 10));
  result.setMinutes(parseInt(minutes, 10));
  return result;
};
var formatDate = (value2) => {
  const date = new Date(value2);
  const year = `000${date.getFullYear()}`.slice(-4);
  const month = `0${date.getMonth() + 1}`.slice(-2);
  const day = `0${date.getDate()}`.slice(-2);
  return `${year}-${month}-${day}`;
};
var formatTime = (value2) => {
  const date = new Date(value2);
  const hours = `0${date.getHours()}`.slice(-2);
  const minutes = `0${date.getMinutes()}`.slice(-2);
  return `${hours}:${minutes}`;
};
var FlexSpaced = styled.div(({ theme }) => ({
  flex: 1,
  display: "flex",
  input: {
    marginLeft: 10,
    flex: 1,
    height: 32,
    // hardcode height bc Chromium bug https://bugs.chromium.org/p/chromium/issues/detail?id=417606
    "&::-webkit-calendar-picker-indicator": {
      opacity: 0.5,
      height: 12,
      filter: theme.base === "light" ? void 0 : "invert(1)"
    }
  },
  "input:first-of-type": {
    marginLeft: 0,
    flexGrow: 4
  },
  "input:last-of-type": {
    flexGrow: 3
  }
}));
var DateControl = ({ name, value: value2, onChange, onFocus, onBlur }) => {
  const [valid, setValid] = useState(true);
  const dateRef = useRef();
  const timeRef = useRef();
  useEffect(() => {
    if (valid !== false) {
      if (dateRef && dateRef.current) {
        dateRef.current.value = formatDate(value2);
      }
      if (timeRef && timeRef.current) {
        timeRef.current.value = formatTime(value2);
      }
    }
  }, [value2]);
  const onDateChange = (e) => {
    const parsed = parseDate(e.target.value);
    const result = new Date(value2);
    result.setFullYear(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    const time = result.getTime();
    if (time)
      onChange(time);
    setValid(!!time);
  };
  const onTimeChange = (e) => {
    const parsed = parseTime(e.target.value);
    const result = new Date(value2);
    result.setHours(parsed.getHours());
    result.setMinutes(parsed.getMinutes());
    const time = result.getTime();
    if (time)
      onChange(time);
    setValid(!!time);
  };
  const controlId = getControlId(name);
  return /* @__PURE__ */ React17.createElement(FlexSpaced, null, /* @__PURE__ */ React17.createElement(
    Form.Input,
    {
      type: "date",
      max: "9999-12-31",
      ref: dateRef,
      id: `${controlId}-date`,
      name: `${controlId}-date`,
      onChange: onDateChange,
      ...{ onFocus, onBlur }
    }
  ), /* @__PURE__ */ React17.createElement(
    Form.Input,
    {
      type: "time",
      id: `${controlId}-time`,
      name: `${controlId}-time`,
      ref: timeRef,
      onChange: onTimeChange,
      ...{ onFocus, onBlur }
    }
  ), !valid ? /* @__PURE__ */ React17.createElement("div", null, "invalid") : null);
};
var Wrapper2 = styled.label({
  display: "flex"
});
var parse2 = (value2) => {
  const result = parseFloat(value2);
  return Number.isNaN(result) ? void 0 : result;
};
var format = (value2) => value2 != null ? String(value2) : "";
var NumberControl = ({
  name,
  value: value2,
  onChange,
  min,
  max,
  step,
  onBlur,
  onFocus
}) => {
  const [inputValue, setInputValue] = useState(typeof value2 === "number" ? value2 : "");
  const [forceVisible, setForceVisible] = useState(false);
  const [parseError, setParseError] = useState(null);
  const handleChange = useCallback(
    (event) => {
      setInputValue(event.target.value);
      const result = parseFloat(event.target.value);
      if (Number.isNaN(result)) {
        setParseError(new Error(`'${event.target.value}' is not a number`));
      } else {
        onChange(result);
        setParseError(null);
      }
    },
    [onChange, setParseError]
  );
  const onForceVisible = useCallback(() => {
    setInputValue("0");
    onChange(0);
    setForceVisible(true);
  }, [setForceVisible]);
  const htmlElRef = useRef(null);
  useEffect(() => {
    if (forceVisible && htmlElRef.current)
      htmlElRef.current.select();
  }, [forceVisible]);
  useEffect(() => {
    const newInputValue = typeof value2 === "number" ? value2 : "";
    if (inputValue !== newInputValue) {
      setInputValue(value2);
    }
  }, [value2]);
  if (!forceVisible && value2 === void 0) {
    return /* @__PURE__ */ React17.createElement(Form.Button, { id: getControlSetterButtonId(name), onClick: onForceVisible }, "Set number");
  }
  return /* @__PURE__ */ React17.createElement(Wrapper2, null, /* @__PURE__ */ React17.createElement(
    Form.Input,
    {
      ref: htmlElRef,
      id: getControlId(name),
      type: "number",
      onChange: handleChange,
      size: "flex",
      placeholder: "Edit number...",
      value: inputValue,
      valid: parseError ? "error" : null,
      autoFocus: forceVisible,
      ...{ name, min, max, step, onFocus, onBlur }
    }
  ));
};

// src/controls/options/helpers.ts
var selectedKey = (value2, options) => {
  const entry = options && Object.entries(options).find(([_key, val]) => val === value2);
  return entry ? entry[0] : void 0;
};
var selectedKeys = (value2, options) => value2 && options ? Object.entries(options).filter((entry) => value2.includes(entry[1])).map((entry) => entry[0]) : [];
var selectedValues = (keys, options) => keys && options && keys.map((key) => options[key]);

// src/controls/options/Checkbox.tsx
var Wrapper3 = styled.div(
  ({ isInline }) => isInline ? {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-start",
    label: {
      display: "inline-flex",
      marginRight: 15
    }
  } : {
    label: {
      display: "flex"
    }
  }
);
var Text2 = styled.span({});
var Label2 = styled.label({
  lineHeight: "20px",
  alignItems: "center",
  marginBottom: 8,
  "&:last-child": {
    marginBottom: 0
  },
  input: {
    margin: 0,
    marginRight: 6
  }
});
var CheckboxControl = ({
  name,
  options,
  value: value2,
  onChange,
  isInline
}) => {
  if (!options) {
    logger.warn(`Checkbox with no options: ${name}`);
    return /* @__PURE__ */ React17.createElement(React17.Fragment, null, "-");
  }
  const initial = selectedKeys(value2, options);
  const [selected, setSelected] = useState(initial);
  const handleChange = (e) => {
    const option = e.target.value;
    const updated = [...selected];
    if (updated.includes(option)) {
      updated.splice(updated.indexOf(option), 1);
    } else {
      updated.push(option);
    }
    onChange(selectedValues(updated, options));
    setSelected(updated);
  };
  useEffect(() => {
    setSelected(selectedKeys(value2, options));
  }, [value2]);
  const controlId = getControlId(name);
  return /* @__PURE__ */ React17.createElement(Wrapper3, { isInline }, Object.keys(options).map((key, index) => {
    const id = `${controlId}-${index}`;
    return /* @__PURE__ */ React17.createElement(Label2, { key: id, htmlFor: id }, /* @__PURE__ */ React17.createElement(
      "input",
      {
        type: "checkbox",
        id,
        name: id,
        value: key,
        onChange: handleChange,
        checked: selected?.includes(key)
      }
    ), /* @__PURE__ */ React17.createElement(Text2, null, key));
  }));
};
var Wrapper4 = styled.div(
  ({ isInline }) => isInline ? {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-start",
    label: {
      display: "inline-flex",
      marginRight: 15
    }
  } : {
    label: {
      display: "flex"
    }
  }
);
var Text3 = styled.span({});
var Label3 = styled.label({
  lineHeight: "20px",
  alignItems: "center",
  marginBottom: 8,
  "&:last-child": {
    marginBottom: 0
  },
  input: {
    margin: 0,
    marginRight: 6
  }
});
var RadioControl = ({ name, options, value: value2, onChange, isInline }) => {
  if (!options) {
    logger.warn(`Radio with no options: ${name}`);
    return /* @__PURE__ */ React17.createElement(React17.Fragment, null, "-");
  }
  const selection = selectedKey(value2, options);
  const controlId = getControlId(name);
  return /* @__PURE__ */ React17.createElement(Wrapper4, { isInline }, Object.keys(options).map((key, index) => {
    const id = `${controlId}-${index}`;
    return /* @__PURE__ */ React17.createElement(Label3, { key: id, htmlFor: id }, /* @__PURE__ */ React17.createElement(
      "input",
      {
        type: "radio",
        id,
        name: id,
        value: key,
        onChange: (e) => onChange(options[e.currentTarget.value]),
        checked: key === selection
      }
    ), /* @__PURE__ */ React17.createElement(Text3, null, key));
  }));
};
var styleResets = {
  // resets
  appearance: "none",
  border: "0 none",
  boxSizing: "inherit",
  display: " block",
  margin: " 0",
  background: "transparent",
  padding: 0,
  fontSize: "inherit",
  position: "relative"
};
var OptionsSelect = styled.select(({ theme }) => ({
  ...styleResets,
  boxSizing: "border-box",
  position: "relative",
  padding: "6px 10px",
  width: "100%",
  color: theme.input.color || "inherit",
  background: theme.input.background,
  borderRadius: theme.input.borderRadius,
  boxShadow: `${theme.input.border} 0 0 0 1px inset`,
  fontSize: theme.typography.size.s2 - 1,
  lineHeight: "20px",
  "&:focus": {
    boxShadow: `${theme.color.secondary} 0 0 0 1px inset`,
    outline: "none"
  },
  "&[disabled]": {
    cursor: "not-allowed",
    opacity: 0.5
  },
  "::placeholder": {
    color: theme.textMutedColor
  },
  "&[multiple]": {
    overflow: "auto",
    padding: 0,
    option: {
      display: "block",
      padding: "6px 10px",
      marginLeft: 1,
      marginRight: 1
    }
  }
}));
var SelectWrapper = styled.span(({ theme }) => ({
  display: "inline-block",
  lineHeight: "normal",
  overflow: "hidden",
  position: "relative",
  verticalAlign: "top",
  width: "100%"
}));
var SelectChevronDown = styled.div(({ theme }) => ({
  position: "absolute",
  zIndex: 1,
  pointerEvents: "none",
  height: "12px",
  marginTop: "-6px",
  right: "12px",
  top: "50%",
  color: theme.textMutedColor
}));
var NO_SELECTION = "Choose option...";
var SingleSelect = ({ name, value: value2, options, onChange }) => {
  const handleChange = (e) => {
    onChange(options[e.currentTarget.value]);
  };
  const selection = selectedKey(value2, options) || NO_SELECTION;
  const controlId = getControlId(name);
  return /* @__PURE__ */ React17.createElement(SelectWrapper, null, /* @__PURE__ */ React17.createElement(SelectChevronDown, null, /* @__PURE__ */ React17.createElement(Icon.ChevronDown, null)), /* @__PURE__ */ React17.createElement(OptionsSelect, { id: controlId, value: selection, onChange: handleChange }, /* @__PURE__ */ React17.createElement("option", { key: "no-selection", disabled: true }, NO_SELECTION), Object.keys(options).map((key) => /* @__PURE__ */ React17.createElement("option", { key }, key))));
};
var MultiSelect = ({ name, value: value2, options, onChange }) => {
  const handleChange = (e) => {
    const selection2 = Array.from(e.currentTarget.options).filter((option) => option.selected).map((option) => option.value);
    onChange(selectedValues(selection2, options));
  };
  const selection = selectedKeys(value2, options);
  const controlId = getControlId(name);
  return /* @__PURE__ */ React17.createElement(SelectWrapper, null, /* @__PURE__ */ React17.createElement(OptionsSelect, { id: controlId, multiple: true, value: selection, onChange: handleChange }, Object.keys(options).map((key) => /* @__PURE__ */ React17.createElement("option", { key }, key))));
};
var SelectControl = (props) => {
  const { name, options } = props;
  if (!options) {
    logger.warn(`Select with no options: ${name}`);
    return /* @__PURE__ */ React17.createElement(React17.Fragment, null, "-");
  }
  return props.isMulti ? /* @__PURE__ */ React17.createElement(MultiSelect, { ...props }) : /* @__PURE__ */ React17.createElement(SingleSelect, { ...props });
};

// src/controls/options/Options.tsx
var normalizeOptions = (options, labels) => {
  if (Array.isArray(options)) {
    return options.reduce((acc, item) => {
      acc[labels?.[item] || String(item)] = item;
      return acc;
    }, {});
  }
  return options;
};
var Controls = {
  check: CheckboxControl,
  "inline-check": CheckboxControl,
  radio: RadioControl,
  "inline-radio": RadioControl,
  select: SelectControl,
  "multi-select": SelectControl
};
var OptionsControl = (props) => {
  const { type = "select", labels, argType } = props;
  const normalized = {
    ...props,
    options: argType ? normalizeOptions(argType.options, labels) : {},
    isInline: type.includes("inline"),
    isMulti: type.includes("multi")
  };
  const Control = Controls[type];
  if (Control) {
    return /* @__PURE__ */ React17.createElement(Control, { ...normalized });
  }
  throw new Error(`Unknown options type: ${type}`);
};

// src/controls/react-editable-json-tree/types/inputUsageTypes.ts
var VALUE = "value";
var KEY = "key";

// src/controls/react-editable-json-tree/types/dataTypes.ts
var ERROR = "Error";
var OBJECT = "Object";
var ARRAY = "Array";
var STRING = "String";
var NUMBER = "Number";
var BOOLEAN = "Boolean";
var DATE = "Date";
var NULL = "Null";
var UNDEFINED = "Undefined";
var FUNCTION = "Function";
var SYMBOL = "Symbol";

// src/controls/react-editable-json-tree/types/deltaTypes.ts
var ADD_DELTA_TYPE = "ADD_DELTA_TYPE";
var REMOVE_DELTA_TYPE = "REMOVE_DELTA_TYPE";
var UPDATE_DELTA_TYPE = "UPDATE_DELTA_TYPE";

// src/controls/react-editable-json-tree/utils/objectTypes.ts
function getObjectType(obj) {
  if (obj !== null && typeof obj === "object" && !Array.isArray(obj) && typeof obj[Symbol.iterator] === "function") {
    return "Iterable";
  }
  return Object.prototype.toString.call(obj).slice(8, -1);
}
function isComponentWillChange(oldValue, newValue) {
  const oldType = getObjectType(oldValue);
  const newType = getObjectType(newValue);
  return (oldType === "Function" || newType === "Function") && newType !== oldType;
}

// src/controls/react-editable-json-tree/JsonNodes.tsx
var JsonAddValue = class extends Component {
  constructor(props) {
    super(props);
    this.state = {
      inputRefKey: null,
      inputRefValue: null
    };
    this.refInputValue = this.refInputValue.bind(this);
    this.refInputKey = this.refInputKey.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }
  componentDidMount() {
    const { inputRefKey, inputRefValue } = this.state;
    const { onlyValue } = this.props;
    if (inputRefKey && typeof inputRefKey.focus === "function") {
      inputRefKey.focus();
    }
    if (onlyValue && inputRefValue && typeof inputRefValue.focus === "function") {
      inputRefValue.focus();
    }
    document.addEventListener("keydown", this.onKeydown);
  }
  componentWillUnmount() {
    document.removeEventListener("keydown", this.onKeydown);
  }
  onKeydown(event) {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.repeat)
      return;
    if (event.code === "Enter" || event.key === "Enter") {
      event.preventDefault();
      this.onSubmit();
    }
    if (event.code === "Escape" || event.key === "Escape") {
      event.preventDefault();
      this.props.handleCancel();
    }
  }
  onSubmit() {
    const { handleAdd, onlyValue, onSubmitValueParser, keyPath, deep } = this.props;
    const { inputRefKey, inputRefValue } = this.state;
    const result = {};
    if (!onlyValue) {
      if (!inputRefKey.value) {
        return;
      }
      result.key = inputRefKey.value;
    }
    result.newValue = onSubmitValueParser(false, keyPath, deep, result.key, inputRefValue.value);
    handleAdd(result);
  }
  refInputKey(node) {
    this.state.inputRefKey = node;
  }
  refInputValue(node) {
    this.state.inputRefValue = node;
  }
  render() {
    const {
      handleCancel,
      onlyValue,
      addButtonElement,
      cancelButtonElement,
      inputElementGenerator,
      keyPath,
      deep
    } = this.props;
    const addButtonElementLayout = cloneElement(addButtonElement, {
      onClick: this.onSubmit
    });
    const cancelButtonElementLayout = cloneElement(cancelButtonElement, {
      onClick: handleCancel
    });
    const inputElementValue = inputElementGenerator(VALUE, keyPath, deep);
    const inputElementValueLayout = cloneElement(inputElementValue, {
      placeholder: "Value",
      ref: this.refInputValue
    });
    let inputElementKeyLayout = null;
    if (!onlyValue) {
      const inputElementKey = inputElementGenerator(KEY, keyPath, deep);
      inputElementKeyLayout = cloneElement(inputElementKey, {
        placeholder: "Key",
        ref: this.refInputKey
      });
    }
    return /* @__PURE__ */ React17.createElement("span", { className: "rejt-add-value-node" }, inputElementKeyLayout, inputElementValueLayout, cancelButtonElementLayout, addButtonElementLayout);
  }
};
JsonAddValue.defaultProps = {
  onlyValue: false,
  addButtonElement: /* @__PURE__ */ React17.createElement("button", null, "+"),
  cancelButtonElement: /* @__PURE__ */ React17.createElement("button", null, "c")
};
var JsonArray = class extends Component {
  constructor(props) {
    super(props);
    const keyPath = [...props.keyPath, props.name];
    this.state = {
      data: props.data,
      name: props.name,
      keyPath,
      deep: props.deep,
      nextDeep: props.deep + 1,
      collapsed: props.isCollapsed(keyPath, props.deep, props.data),
      addFormVisible: false
    };
    this.handleCollapseMode = this.handleCollapseMode.bind(this);
    this.handleRemoveItem = this.handleRemoveItem.bind(this);
    this.handleAddMode = this.handleAddMode.bind(this);
    this.handleAddValueAdd = this.handleAddValueAdd.bind(this);
    this.handleAddValueCancel = this.handleAddValueCancel.bind(this);
    this.handleEditValue = this.handleEditValue.bind(this);
    this.onChildUpdate = this.onChildUpdate.bind(this);
    this.renderCollapsed = this.renderCollapsed.bind(this);
    this.renderNotCollapsed = this.renderNotCollapsed.bind(this);
  }
  static getDerivedStateFromProps(props, state) {
    return props.data !== state.data ? { data: props.data } : null;
  }
  onChildUpdate(childKey, childData) {
    const { data, keyPath } = this.state;
    data[childKey] = childData;
    this.setState({
      data
    });
    const { onUpdate } = this.props;
    const size = keyPath.length;
    onUpdate(keyPath[size - 1], data);
  }
  handleAddMode() {
    this.setState({
      addFormVisible: true
    });
  }
  handleCollapseMode() {
    this.setState((state) => ({
      collapsed: !state.collapsed
    }));
  }
  handleRemoveItem(index) {
    return () => {
      const { beforeRemoveAction, logger: logger4 } = this.props;
      const { data, keyPath, nextDeep: deep } = this.state;
      const oldValue = data[index];
      beforeRemoveAction(index, keyPath, deep, oldValue).then(() => {
        const deltaUpdateResult = {
          keyPath,
          deep,
          key: index,
          oldValue,
          type: REMOVE_DELTA_TYPE
        };
        data.splice(index, 1);
        this.setState({ data });
        const { onUpdate, onDeltaUpdate } = this.props;
        onUpdate(keyPath[keyPath.length - 1], data);
        onDeltaUpdate(deltaUpdateResult);
      }).catch(logger4.error);
    };
  }
  handleAddValueAdd({ newValue }) {
    const { data, keyPath, nextDeep: deep } = this.state;
    const { beforeAddAction, logger: logger4 } = this.props;
    beforeAddAction(data.length, keyPath, deep, newValue).then(() => {
      const newData = [...data, newValue];
      this.setState({
        data: newData
      });
      this.handleAddValueCancel();
      const { onUpdate, onDeltaUpdate } = this.props;
      onUpdate(keyPath[keyPath.length - 1], newData);
      onDeltaUpdate({
        type: ADD_DELTA_TYPE,
        keyPath,
        deep,
        key: newData.length - 1,
        newValue
      });
    }).catch(logger4.error);
  }
  handleAddValueCancel() {
    this.setState({
      addFormVisible: false
    });
  }
  handleEditValue({ key, value: value2 }) {
    return new Promise((resolve, reject) => {
      const { beforeUpdateAction } = this.props;
      const { data, keyPath, nextDeep: deep } = this.state;
      const oldValue = data[key];
      beforeUpdateAction(key, keyPath, deep, oldValue, value2).then(() => {
        data[key] = value2;
        this.setState({
          data
        });
        const { onUpdate, onDeltaUpdate } = this.props;
        onUpdate(keyPath[keyPath.length - 1], data);
        onDeltaUpdate({
          type: UPDATE_DELTA_TYPE,
          keyPath,
          deep,
          key,
          newValue: value2,
          oldValue
        });
        resolve(void 0);
      }).catch(reject);
    });
  }
  renderCollapsed() {
    const { name, data, keyPath, deep } = this.state;
    const { handleRemove, readOnly, getStyle, dataType, minusMenuElement } = this.props;
    const { minus, collapsed } = getStyle(name, data, keyPath, deep, dataType);
    const isReadOnly = readOnly(name, data, keyPath, deep, dataType);
    const removeItemButton = cloneElement(minusMenuElement, {
      onClick: handleRemove,
      className: "rejt-minus-menu",
      style: minus
    });
    return /* @__PURE__ */ React17.createElement("span", { className: "rejt-collapsed" }, /* @__PURE__ */ React17.createElement("span", { className: "rejt-collapsed-text", style: collapsed, onClick: this.handleCollapseMode }, "[...] ", data.length, " ", data.length === 1 ? "item" : "items"), !isReadOnly && removeItemButton);
  }
  renderNotCollapsed() {
    const { name, data, keyPath, deep, addFormVisible, nextDeep } = this.state;
    const {
      isCollapsed,
      handleRemove,
      onDeltaUpdate,
      readOnly,
      getStyle,
      dataType,
      addButtonElement,
      cancelButtonElement,
      editButtonElement,
      inputElementGenerator,
      textareaElementGenerator,
      minusMenuElement,
      plusMenuElement,
      beforeRemoveAction,
      beforeAddAction,
      beforeUpdateAction,
      logger: logger4,
      onSubmitValueParser
    } = this.props;
    const { minus, plus, delimiter, ul, addForm } = getStyle(name, data, keyPath, deep, dataType);
    const isReadOnly = readOnly(name, data, keyPath, deep, dataType);
    const addItemButton = cloneElement(plusMenuElement, {
      onClick: this.handleAddMode,
      className: "rejt-plus-menu",
      style: plus
    });
    const removeItemButton = cloneElement(minusMenuElement, {
      onClick: handleRemove,
      className: "rejt-minus-menu",
      style: minus
    });
    const onlyValue = true;
    const startObject = "[";
    const endObject = "]";
    return /* @__PURE__ */ React17.createElement("span", { className: "rejt-not-collapsed" }, /* @__PURE__ */ React17.createElement("span", { className: "rejt-not-collapsed-delimiter", style: delimiter }, startObject), !addFormVisible && addItemButton, /* @__PURE__ */ React17.createElement("ul", { className: "rejt-not-collapsed-list", style: ul }, data.map((item, index) => /* @__PURE__ */ React17.createElement(
      JsonNode,
      {
        key: index,
        name: index.toString(),
        data: item,
        keyPath,
        deep: nextDeep,
        isCollapsed,
        handleRemove: this.handleRemoveItem(index),
        handleUpdateValue: this.handleEditValue,
        onUpdate: this.onChildUpdate,
        onDeltaUpdate,
        readOnly,
        getStyle,
        addButtonElement,
        cancelButtonElement,
        editButtonElement,
        inputElementGenerator,
        textareaElementGenerator,
        minusMenuElement,
        plusMenuElement,
        beforeRemoveAction,
        beforeAddAction,
        beforeUpdateAction,
        logger: logger4,
        onSubmitValueParser
      }
    ))), !isReadOnly && addFormVisible && /* @__PURE__ */ React17.createElement("div", { className: "rejt-add-form", style: addForm }, /* @__PURE__ */ React17.createElement(
      JsonAddValue,
      {
        handleAdd: this.handleAddValueAdd,
        handleCancel: this.handleAddValueCancel,
        onlyValue,
        addButtonElement,
        cancelButtonElement,
        inputElementGenerator,
        keyPath,
        deep,
        onSubmitValueParser
      }
    )), /* @__PURE__ */ React17.createElement("span", { className: "rejt-not-collapsed-delimiter", style: delimiter }, endObject), !isReadOnly && removeItemButton);
  }
  render() {
    const { name, collapsed, data, keyPath, deep } = this.state;
    const { dataType, getStyle } = this.props;
    const value2 = collapsed ? this.renderCollapsed() : this.renderNotCollapsed();
    const style = getStyle(name, data, keyPath, deep, dataType);
    return /* @__PURE__ */ React17.createElement("div", { className: "rejt-array-node" }, /* @__PURE__ */ React17.createElement("span", { onClick: this.handleCollapseMode }, /* @__PURE__ */ React17.createElement("span", { className: "rejt-name", style: style.name }, name, " :", " ")), value2);
  }
};
JsonArray.defaultProps = {
  keyPath: [],
  deep: 0,
  minusMenuElement: /* @__PURE__ */ React17.createElement("span", null, " - "),
  plusMenuElement: /* @__PURE__ */ React17.createElement("span", null, " + ")
};
var JsonFunctionValue = class extends Component {
  constructor(props) {
    super(props);
    const keyPath = [...props.keyPath, props.name];
    this.state = {
      value: props.value,
      name: props.name,
      keyPath,
      deep: props.deep,
      editEnabled: false,
      inputRef: null
    };
    this.handleEditMode = this.handleEditMode.bind(this);
    this.refInput = this.refInput.bind(this);
    this.handleCancelEdit = this.handleCancelEdit.bind(this);
    this.handleEdit = this.handleEdit.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
  }
  static getDerivedStateFromProps(props, state) {
    return props.value !== state.value ? { value: props.value } : null;
  }
  componentDidUpdate() {
    const { editEnabled, inputRef, name, value: value2, keyPath, deep } = this.state;
    const { readOnly, dataType } = this.props;
    const readOnlyResult = readOnly(name, value2, keyPath, deep, dataType);
    if (editEnabled && !readOnlyResult && typeof inputRef.focus === "function") {
      inputRef.focus();
    }
  }
  componentDidMount() {
    document.addEventListener("keydown", this.onKeydown);
  }
  componentWillUnmount() {
    document.removeEventListener("keydown", this.onKeydown);
  }
  onKeydown(event) {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.repeat)
      return;
    if (event.code === "Enter" || event.key === "Enter") {
      event.preventDefault();
      this.handleEdit();
    }
    if (event.code === "Escape" || event.key === "Escape") {
      event.preventDefault();
      this.handleCancelEdit();
    }
  }
  handleEdit() {
    const { handleUpdateValue, originalValue, logger: logger4, onSubmitValueParser, keyPath } = this.props;
    const { inputRef, name, deep } = this.state;
    if (!inputRef)
      return;
    const newValue = onSubmitValueParser(true, keyPath, deep, name, inputRef.value);
    const result = {
      value: newValue,
      key: name
    };
    handleUpdateValue(result).then(() => {
      if (!isComponentWillChange(originalValue, newValue)) {
        this.handleCancelEdit();
      }
    }).catch(logger4.error);
  }
  handleEditMode() {
    this.setState({
      editEnabled: true
    });
  }
  refInput(node) {
    this.state.inputRef = node;
  }
  handleCancelEdit() {
    this.setState({
      editEnabled: false
    });
  }
  render() {
    const { name, value: value2, editEnabled, keyPath, deep } = this.state;
    const {
      handleRemove,
      originalValue,
      readOnly,
      dataType,
      getStyle,
      editButtonElement,
      cancelButtonElement,
      textareaElementGenerator,
      minusMenuElement,
      keyPath: comeFromKeyPath
    } = this.props;
    const style = getStyle(name, originalValue, keyPath, deep, dataType);
    let result = null;
    let minusElement = null;
    const resultOnlyResult = readOnly(name, originalValue, keyPath, deep, dataType);
    if (editEnabled && !resultOnlyResult) {
      const textareaElement = textareaElementGenerator(
        VALUE,
        comeFromKeyPath,
        deep,
        name,
        originalValue,
        dataType
      );
      const editButtonElementLayout = cloneElement(editButtonElement, {
        onClick: this.handleEdit
      });
      const cancelButtonElementLayout = cloneElement(cancelButtonElement, {
        onClick: this.handleCancelEdit
      });
      const textareaElementLayout = cloneElement(textareaElement, {
        ref: this.refInput,
        defaultValue: originalValue
      });
      result = /* @__PURE__ */ React17.createElement("span", { className: "rejt-edit-form", style: style.editForm }, textareaElementLayout, " ", cancelButtonElementLayout, editButtonElementLayout);
      minusElement = null;
    } else {
      result = /* @__PURE__ */ React17.createElement(
        "span",
        {
          className: "rejt-value",
          style: style.value,
          onClick: resultOnlyResult ? null : this.handleEditMode
        },
        value2
      );
      const minusMenuLayout = cloneElement(minusMenuElement, {
        onClick: handleRemove,
        className: "rejt-minus-menu",
        style: style.minus
      });
      minusElement = resultOnlyResult ? null : minusMenuLayout;
    }
    return /* @__PURE__ */ React17.createElement("li", { className: "rejt-function-value-node", style: style.li }, /* @__PURE__ */ React17.createElement("span", { className: "rejt-name", style: style.name }, name, " :", " "), result, minusElement);
  }
};
JsonFunctionValue.defaultProps = {
  keyPath: [],
  deep: 0,
  handleUpdateValue: () => {
  },
  editButtonElement: /* @__PURE__ */ React17.createElement("button", null, "e"),
  cancelButtonElement: /* @__PURE__ */ React17.createElement("button", null, "c"),
  minusMenuElement: /* @__PURE__ */ React17.createElement("span", null, " - ")
};
var JsonNode = class extends Component {
  constructor(props) {
    super(props);
    this.state = {
      data: props.data,
      name: props.name,
      keyPath: props.keyPath,
      deep: props.deep
    };
  }
  static getDerivedStateFromProps(props, state) {
    return props.data !== state.data ? { data: props.data } : null;
  }
  render() {
    const { data, name, keyPath, deep } = this.state;
    const {
      isCollapsed,
      handleRemove,
      handleUpdateValue,
      onUpdate,
      onDeltaUpdate,
      readOnly,
      getStyle,
      addButtonElement,
      cancelButtonElement,
      editButtonElement,
      inputElementGenerator,
      textareaElementGenerator,
      minusMenuElement,
      plusMenuElement,
      beforeRemoveAction,
      beforeAddAction,
      beforeUpdateAction,
      logger: logger4,
      onSubmitValueParser
    } = this.props;
    const readOnlyTrue = () => true;
    const dataType = getObjectType(data);
    switch (dataType) {
      case ERROR:
        return /* @__PURE__ */ React17.createElement(
          JsonObject,
          {
            data,
            name,
            isCollapsed,
            keyPath,
            deep,
            handleRemove,
            onUpdate,
            onDeltaUpdate,
            readOnly: readOnlyTrue,
            dataType,
            getStyle,
            addButtonElement,
            cancelButtonElement,
            editButtonElement,
            inputElementGenerator,
            textareaElementGenerator,
            minusMenuElement,
            plusMenuElement,
            beforeRemoveAction,
            beforeAddAction,
            beforeUpdateAction,
            logger: logger4,
            onSubmitValueParser
          }
        );
      case OBJECT:
        return /* @__PURE__ */ React17.createElement(
          JsonObject,
          {
            data,
            name,
            isCollapsed,
            keyPath,
            deep,
            handleRemove,
            onUpdate,
            onDeltaUpdate,
            readOnly,
            dataType,
            getStyle,
            addButtonElement,
            cancelButtonElement,
            editButtonElement,
            inputElementGenerator,
            textareaElementGenerator,
            minusMenuElement,
            plusMenuElement,
            beforeRemoveAction,
            beforeAddAction,
            beforeUpdateAction,
            logger: logger4,
            onSubmitValueParser
          }
        );
      case ARRAY:
        return /* @__PURE__ */ React17.createElement(
          JsonArray,
          {
            data,
            name,
            isCollapsed,
            keyPath,
            deep,
            handleRemove,
            onUpdate,
            onDeltaUpdate,
            readOnly,
            dataType,
            getStyle,
            addButtonElement,
            cancelButtonElement,
            editButtonElement,
            inputElementGenerator,
            textareaElementGenerator,
            minusMenuElement,
            plusMenuElement,
            beforeRemoveAction,
            beforeAddAction,
            beforeUpdateAction,
            logger: logger4,
            onSubmitValueParser
          }
        );
      case STRING:
        return /* @__PURE__ */ React17.createElement(
          JsonValue,
          {
            name,
            value: `"${data}"`,
            originalValue: data,
            keyPath,
            deep,
            handleRemove,
            handleUpdateValue,
            readOnly,
            dataType,
            getStyle,
            cancelButtonElement,
            editButtonElement,
            inputElementGenerator,
            minusMenuElement,
            logger: logger4,
            onSubmitValueParser
          }
        );
      case NUMBER:
        return /* @__PURE__ */ React17.createElement(
          JsonValue,
          {
            name,
            value: data,
            originalValue: data,
            keyPath,
            deep,
            handleRemove,
            handleUpdateValue,
            readOnly,
            dataType,
            getStyle,
            cancelButtonElement,
            editButtonElement,
            inputElementGenerator,
            minusMenuElement,
            logger: logger4,
            onSubmitValueParser
          }
        );
      case BOOLEAN:
        return /* @__PURE__ */ React17.createElement(
          JsonValue,
          {
            name,
            value: data ? "true" : "false",
            originalValue: data,
            keyPath,
            deep,
            handleRemove,
            handleUpdateValue,
            readOnly,
            dataType,
            getStyle,
            cancelButtonElement,
            editButtonElement,
            inputElementGenerator,
            minusMenuElement,
            logger: logger4,
            onSubmitValueParser
          }
        );
      case DATE:
        return /* @__PURE__ */ React17.createElement(
          JsonValue,
          {
            name,
            value: data.toISOString(),
            originalValue: data,
            keyPath,
            deep,
            handleRemove,
            handleUpdateValue,
            readOnly: readOnlyTrue,
            dataType,
            getStyle,
            cancelButtonElement,
            editButtonElement,
            inputElementGenerator,
            minusMenuElement,
            logger: logger4,
            onSubmitValueParser
          }
        );
      case NULL:
        return /* @__PURE__ */ React17.createElement(
          JsonValue,
          {
            name,
            value: "null",
            originalValue: "null",
            keyPath,
            deep,
            handleRemove,
            handleUpdateValue,
            readOnly,
            dataType,
            getStyle,
            cancelButtonElement,
            editButtonElement,
            inputElementGenerator,
            minusMenuElement,
            logger: logger4,
            onSubmitValueParser
          }
        );
      case UNDEFINED:
        return /* @__PURE__ */ React17.createElement(
          JsonValue,
          {
            name,
            value: "undefined",
            originalValue: "undefined",
            keyPath,
            deep,
            handleRemove,
            handleUpdateValue,
            readOnly,
            dataType,
            getStyle,
            cancelButtonElement,
            editButtonElement,
            inputElementGenerator,
            minusMenuElement,
            logger: logger4,
            onSubmitValueParser
          }
        );
      case FUNCTION:
        return /* @__PURE__ */ React17.createElement(
          JsonFunctionValue,
          {
            name,
            value: data.toString(),
            originalValue: data,
            keyPath,
            deep,
            handleRemove,
            handleUpdateValue,
            readOnly,
            dataType,
            getStyle,
            cancelButtonElement,
            editButtonElement,
            textareaElementGenerator,
            minusMenuElement,
            logger: logger4,
            onSubmitValueParser
          }
        );
      case SYMBOL:
        return /* @__PURE__ */ React17.createElement(
          JsonValue,
          {
            name,
            value: data.toString(),
            originalValue: data,
            keyPath,
            deep,
            handleRemove,
            handleUpdateValue,
            readOnly: readOnlyTrue,
            dataType,
            getStyle,
            cancelButtonElement,
            editButtonElement,
            inputElementGenerator,
            minusMenuElement,
            logger: logger4,
            onSubmitValueParser
          }
        );
      default:
        return null;
    }
  }
};
JsonNode.defaultProps = {
  keyPath: [],
  deep: 0
};
var JsonObject = class extends Component {
  constructor(props) {
    super(props);
    const keyPath = props.deep === -1 ? [] : [...props.keyPath, props.name];
    this.state = {
      name: props.name,
      data: props.data,
      keyPath,
      deep: props.deep,
      nextDeep: props.deep + 1,
      collapsed: props.isCollapsed(keyPath, props.deep, props.data),
      addFormVisible: false
    };
    this.handleCollapseMode = this.handleCollapseMode.bind(this);
    this.handleRemoveValue = this.handleRemoveValue.bind(this);
    this.handleAddMode = this.handleAddMode.bind(this);
    this.handleAddValueAdd = this.handleAddValueAdd.bind(this);
    this.handleAddValueCancel = this.handleAddValueCancel.bind(this);
    this.handleEditValue = this.handleEditValue.bind(this);
    this.onChildUpdate = this.onChildUpdate.bind(this);
    this.renderCollapsed = this.renderCollapsed.bind(this);
    this.renderNotCollapsed = this.renderNotCollapsed.bind(this);
  }
  static getDerivedStateFromProps(props, state) {
    return props.data !== state.data ? { data: props.data } : null;
  }
  onChildUpdate(childKey, childData) {
    const { data, keyPath } = this.state;
    data[childKey] = childData;
    this.setState({
      data
    });
    const { onUpdate } = this.props;
    const size = keyPath.length;
    onUpdate(keyPath[size - 1], data);
  }
  handleAddMode() {
    this.setState({
      addFormVisible: true
    });
  }
  handleAddValueCancel() {
    this.setState({
      addFormVisible: false
    });
  }
  handleAddValueAdd({ key, newValue }) {
    const { data, keyPath, nextDeep: deep } = this.state;
    const { beforeAddAction, logger: logger4 } = this.props;
    beforeAddAction(key, keyPath, deep, newValue).then(() => {
      data[key] = newValue;
      this.setState({
        data
      });
      this.handleAddValueCancel();
      const { onUpdate, onDeltaUpdate } = this.props;
      onUpdate(keyPath[keyPath.length - 1], data);
      onDeltaUpdate({
        type: ADD_DELTA_TYPE,
        keyPath,
        deep,
        key,
        newValue
      });
    }).catch(logger4.error);
  }
  handleRemoveValue(key) {
    return () => {
      const { beforeRemoveAction, logger: logger4 } = this.props;
      const { data, keyPath, nextDeep: deep } = this.state;
      const oldValue = data[key];
      beforeRemoveAction(key, keyPath, deep, oldValue).then(() => {
        const deltaUpdateResult = {
          keyPath,
          deep,
          key,
          oldValue,
          type: REMOVE_DELTA_TYPE
        };
        delete data[key];
        this.setState({ data });
        const { onUpdate, onDeltaUpdate } = this.props;
        onUpdate(keyPath[keyPath.length - 1], data);
        onDeltaUpdate(deltaUpdateResult);
      }).catch(logger4.error);
    };
  }
  handleCollapseMode() {
    this.setState((state) => ({
      collapsed: !state.collapsed
    }));
  }
  handleEditValue({ key, value: value2 }) {
    return new Promise((resolve, reject) => {
      const { beforeUpdateAction } = this.props;
      const { data, keyPath, nextDeep: deep } = this.state;
      const oldValue = data[key];
      beforeUpdateAction(key, keyPath, deep, oldValue, value2).then(() => {
        data[key] = value2;
        this.setState({
          data
        });
        const { onUpdate, onDeltaUpdate } = this.props;
        onUpdate(keyPath[keyPath.length - 1], data);
        onDeltaUpdate({
          type: UPDATE_DELTA_TYPE,
          keyPath,
          deep,
          key,
          newValue: value2,
          oldValue
        });
        resolve();
      }).catch(reject);
    });
  }
  renderCollapsed() {
    const { name, keyPath, deep, data } = this.state;
    const { handleRemove, readOnly, dataType, getStyle, minusMenuElement } = this.props;
    const { minus, collapsed } = getStyle(name, data, keyPath, deep, dataType);
    const keyList = Object.getOwnPropertyNames(data);
    const isReadOnly = readOnly(name, data, keyPath, deep, dataType);
    const removeItemButton = cloneElement(minusMenuElement, {
      onClick: handleRemove,
      className: "rejt-minus-menu",
      style: minus
    });
    return /* @__PURE__ */ React17.createElement("span", { className: "rejt-collapsed" }, /* @__PURE__ */ React17.createElement("span", { className: "rejt-collapsed-text", style: collapsed, onClick: this.handleCollapseMode }, "{...}", " ", keyList.length, " ", keyList.length === 1 ? "key" : "keys"), !isReadOnly && removeItemButton);
  }
  renderNotCollapsed() {
    const { name, data, keyPath, deep, nextDeep, addFormVisible } = this.state;
    const {
      isCollapsed,
      handleRemove,
      onDeltaUpdate,
      readOnly,
      getStyle,
      dataType,
      addButtonElement,
      cancelButtonElement,
      editButtonElement,
      inputElementGenerator,
      textareaElementGenerator,
      minusMenuElement,
      plusMenuElement,
      beforeRemoveAction,
      beforeAddAction,
      beforeUpdateAction,
      logger: logger4,
      onSubmitValueParser
    } = this.props;
    const { minus, plus, addForm, ul, delimiter } = getStyle(name, data, keyPath, deep, dataType);
    const keyList = Object.getOwnPropertyNames(data);
    const isReadOnly = readOnly(name, data, keyPath, deep, dataType);
    const addItemButton = cloneElement(plusMenuElement, {
      onClick: this.handleAddMode,
      className: "rejt-plus-menu",
      style: plus
    });
    const removeItemButton = cloneElement(minusMenuElement, {
      onClick: handleRemove,
      className: "rejt-minus-menu",
      style: minus
    });
    const list = keyList.map((key) => /* @__PURE__ */ React17.createElement(
      JsonNode,
      {
        key,
        name: key,
        data: data[key],
        keyPath,
        deep: nextDeep,
        isCollapsed,
        handleRemove: this.handleRemoveValue(key),
        handleUpdateValue: this.handleEditValue,
        onUpdate: this.onChildUpdate,
        onDeltaUpdate,
        readOnly,
        getStyle,
        addButtonElement,
        cancelButtonElement,
        editButtonElement,
        inputElementGenerator,
        textareaElementGenerator,
        minusMenuElement,
        plusMenuElement,
        beforeRemoveAction,
        beforeAddAction,
        beforeUpdateAction,
        logger: logger4,
        onSubmitValueParser
      }
    ));
    const startObject = "{";
    const endObject = "}";
    return /* @__PURE__ */ React17.createElement("span", { className: "rejt-not-collapsed" }, /* @__PURE__ */ React17.createElement("span", { className: "rejt-not-collapsed-delimiter", style: delimiter }, startObject), !isReadOnly && addItemButton, /* @__PURE__ */ React17.createElement("ul", { className: "rejt-not-collapsed-list", style: ul }, list), !isReadOnly && addFormVisible && /* @__PURE__ */ React17.createElement("div", { className: "rejt-add-form", style: addForm }, /* @__PURE__ */ React17.createElement(
      JsonAddValue,
      {
        handleAdd: this.handleAddValueAdd,
        handleCancel: this.handleAddValueCancel,
        addButtonElement,
        cancelButtonElement,
        inputElementGenerator,
        keyPath,
        deep,
        onSubmitValueParser
      }
    )), /* @__PURE__ */ React17.createElement("span", { className: "rejt-not-collapsed-delimiter", style: delimiter }, endObject), !isReadOnly && removeItemButton);
  }
  render() {
    const { name, collapsed, data, keyPath, deep } = this.state;
    const { getStyle, dataType } = this.props;
    const value2 = collapsed ? this.renderCollapsed() : this.renderNotCollapsed();
    const style = getStyle(name, data, keyPath, deep, dataType);
    return /* @__PURE__ */ React17.createElement("div", { className: "rejt-object-node" }, /* @__PURE__ */ React17.createElement("span", { onClick: this.handleCollapseMode }, /* @__PURE__ */ React17.createElement("span", { className: "rejt-name", style: style.name }, name, " :", " ")), value2);
  }
};
JsonObject.defaultProps = {
  keyPath: [],
  deep: 0,
  minusMenuElement: /* @__PURE__ */ React17.createElement("span", null, " - "),
  plusMenuElement: /* @__PURE__ */ React17.createElement("span", null, " + ")
};
var JsonValue = class extends Component {
  constructor(props) {
    super(props);
    const keyPath = [...props.keyPath, props.name];
    this.state = {
      value: props.value,
      name: props.name,
      keyPath,
      deep: props.deep,
      editEnabled: false,
      inputRef: null
    };
    this.handleEditMode = this.handleEditMode.bind(this);
    this.refInput = this.refInput.bind(this);
    this.handleCancelEdit = this.handleCancelEdit.bind(this);
    this.handleEdit = this.handleEdit.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
  }
  static getDerivedStateFromProps(props, state) {
    return props.value !== state.value ? { value: props.value } : null;
  }
  componentDidUpdate() {
    const { editEnabled, inputRef, name, value: value2, keyPath, deep } = this.state;
    const { readOnly, dataType } = this.props;
    const isReadOnly = readOnly(name, value2, keyPath, deep, dataType);
    if (editEnabled && !isReadOnly && typeof inputRef.focus === "function") {
      inputRef.focus();
    }
  }
  componentDidMount() {
    document.addEventListener("keydown", this.onKeydown);
  }
  componentWillUnmount() {
    document.removeEventListener("keydown", this.onKeydown);
  }
  onKeydown(event) {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.repeat)
      return;
    if (event.code === "Enter" || event.key === "Enter") {
      event.preventDefault();
      this.handleEdit();
    }
    if (event.code === "Escape" || event.key === "Escape") {
      event.preventDefault();
      this.handleCancelEdit();
    }
  }
  handleEdit() {
    const { handleUpdateValue, originalValue, logger: logger4, onSubmitValueParser, keyPath } = this.props;
    const { inputRef, name, deep } = this.state;
    if (!inputRef)
      return;
    const newValue = onSubmitValueParser(true, keyPath, deep, name, inputRef.value);
    const result = {
      value: newValue,
      key: name
    };
    handleUpdateValue(result).then(() => {
      if (!isComponentWillChange(originalValue, newValue)) {
        this.handleCancelEdit();
      }
    }).catch(logger4.error);
  }
  handleEditMode() {
    this.setState({
      editEnabled: true
    });
  }
  refInput(node) {
    this.state.inputRef = node;
  }
  handleCancelEdit() {
    this.setState({
      editEnabled: false
    });
  }
  render() {
    const { name, value: value2, editEnabled, keyPath, deep } = this.state;
    const {
      handleRemove,
      originalValue,
      readOnly,
      dataType,
      getStyle,
      editButtonElement,
      cancelButtonElement,
      inputElementGenerator,
      minusMenuElement,
      keyPath: comeFromKeyPath
    } = this.props;
    const style = getStyle(name, originalValue, keyPath, deep, dataType);
    const isReadOnly = readOnly(name, originalValue, keyPath, deep, dataType);
    const isEditing = editEnabled && !isReadOnly;
    const inputElement = inputElementGenerator(
      VALUE,
      comeFromKeyPath,
      deep,
      name,
      originalValue,
      dataType
    );
    const editButtonElementLayout = cloneElement(editButtonElement, {
      onClick: this.handleEdit
    });
    const cancelButtonElementLayout = cloneElement(cancelButtonElement, {
      onClick: this.handleCancelEdit
    });
    const inputElementLayout = cloneElement(inputElement, {
      ref: this.refInput,
      defaultValue: JSON.stringify(originalValue)
    });
    const minusMenuLayout = cloneElement(minusMenuElement, {
      onClick: handleRemove,
      className: "rejt-minus-menu",
      style: style.minus
    });
    return /* @__PURE__ */ React17.createElement("li", { className: "rejt-value-node", style: style.li }, /* @__PURE__ */ React17.createElement("span", { className: "rejt-name", style: style.name }, name, " : "), isEditing ? /* @__PURE__ */ React17.createElement("span", { className: "rejt-edit-form", style: style.editForm }, inputElementLayout, " ", cancelButtonElementLayout, editButtonElementLayout) : /* @__PURE__ */ React17.createElement(
      "span",
      {
        className: "rejt-value",
        style: style.value,
        onClick: isReadOnly ? null : this.handleEditMode
      },
      String(value2)
    ), !isReadOnly && !isEditing && minusMenuLayout);
  }
};
JsonValue.defaultProps = {
  keyPath: [],
  deep: 0,
  handleUpdateValue: () => Promise.resolve(),
  editButtonElement: /* @__PURE__ */ React17.createElement("button", null, "e"),
  cancelButtonElement: /* @__PURE__ */ React17.createElement("button", null, "c"),
  minusMenuElement: /* @__PURE__ */ React17.createElement("span", null, " - ")
};

// src/controls/react-editable-json-tree/utils/styles.ts
var object = {
  minus: {
    color: "red"
  },
  plus: {
    color: "green"
  },
  collapsed: {
    color: "grey"
  },
  delimiter: {},
  ul: {
    padding: "0px",
    margin: "0 0 0 25px",
    listStyle: "none"
  },
  name: {
    color: "#2287CD"
  },
  addForm: {}
};
var array = {
  minus: {
    color: "red"
  },
  plus: {
    color: "green"
  },
  collapsed: {
    color: "grey"
  },
  delimiter: {},
  ul: {
    padding: "0px",
    margin: "0 0 0 25px",
    listStyle: "none"
  },
  name: {
    color: "#2287CD"
  },
  addForm: {}
};
var value = {
  minus: {
    color: "red"
  },
  editForm: {},
  value: {
    color: "#7bba3d"
  },
  li: {
    minHeight: "22px",
    lineHeight: "22px",
    outline: "0px"
  },
  name: {
    color: "#2287CD"
  }
};

// src/controls/react-editable-json-tree/utils/parse.ts
function parse3(string) {
  let result = string;
  if (result.indexOf("function") === 0) {
    return (0, eval)(`(${result})`);
  }
  try {
    result = JSON.parse(string);
  } catch (e) {
  }
  return result;
}

// src/controls/react-editable-json-tree/index.tsx
var JsonTree = class extends Component {
  constructor(props) {
    super(props);
    this.state = {
      data: props.data,
      rootName: props.rootName
    };
    this.onUpdate = this.onUpdate.bind(this);
    this.removeRoot = this.removeRoot.bind(this);
  }
  static getDerivedStateFromProps(props, state) {
    if (props.data !== state.data || props.rootName !== state.rootName) {
      return {
        data: props.data,
        rootName: props.rootName
      };
    }
    return null;
  }
  onUpdate(key, data) {
    this.setState({ data });
    this.props.onFullyUpdate(data);
  }
  removeRoot() {
    this.onUpdate(null, null);
  }
  render() {
    const { data, rootName } = this.state;
    const {
      isCollapsed,
      onDeltaUpdate,
      readOnly,
      getStyle,
      addButtonElement,
      cancelButtonElement,
      editButtonElement,
      inputElement,
      textareaElement,
      minusMenuElement,
      plusMenuElement,
      beforeRemoveAction,
      beforeAddAction,
      beforeUpdateAction,
      logger: logger4,
      onSubmitValueParser,
      fallback = null
    } = this.props;
    const dataType = getObjectType(data);
    let readOnlyFunction = readOnly;
    if (getObjectType(readOnly) === "Boolean") {
      readOnlyFunction = () => readOnly;
    }
    let inputElementFunction = inputElement;
    if (inputElement && getObjectType(inputElement) !== "Function") {
      inputElementFunction = () => inputElement;
    }
    let textareaElementFunction = textareaElement;
    if (textareaElement && getObjectType(textareaElement) !== "Function") {
      textareaElementFunction = () => textareaElement;
    }
    if (dataType === "Object" || dataType === "Array") {
      return /* @__PURE__ */ React17.createElement("div", { className: "rejt-tree" }, /* @__PURE__ */ React17.createElement(
        JsonNode,
        {
          data,
          name: rootName,
          deep: -1,
          isCollapsed,
          onUpdate: this.onUpdate,
          onDeltaUpdate,
          readOnly: readOnlyFunction,
          getStyle,
          addButtonElement,
          cancelButtonElement,
          editButtonElement,
          inputElementGenerator: inputElementFunction,
          textareaElementGenerator: textareaElementFunction,
          minusMenuElement,
          plusMenuElement,
          handleRemove: this.removeRoot,
          beforeRemoveAction,
          beforeAddAction,
          beforeUpdateAction,
          logger: logger4,
          onSubmitValueParser
        }
      ));
    }
    return fallback;
  }
};
JsonTree.defaultProps = {
  rootName: "root",
  isCollapsed: (keyPath, deep) => deep !== -1,
  getStyle: (keyName, data, keyPath, deep, dataType) => {
    switch (dataType) {
      case "Object":
      case "Error":
        return object;
      case "Array":
        return array;
      default:
        return value;
    }
  },
  readOnly: () => false,
  onFullyUpdate: () => {
  },
  onDeltaUpdate: () => {
  },
  beforeRemoveAction: () => Promise.resolve(),
  beforeAddAction: () => Promise.resolve(),
  beforeUpdateAction: () => Promise.resolve(),
  logger: { error: () => {
  } },
  onSubmitValueParser: (isEditMode, keyPath, deep, name, rawValue) => parse3(rawValue),
  inputElement: () => /* @__PURE__ */ React17.createElement("input", null),
  textareaElement: () => /* @__PURE__ */ React17.createElement("textarea", null),
  fallback: null
};

// src/controls/Object.tsx
var { window: globalWindow2 } = global;
var Wrapper5 = styled.div(({ theme }) => ({
  position: "relative",
  display: "flex",
  ".rejt-tree": {
    marginLeft: "1rem",
    fontSize: "13px"
  },
  ".rejt-value-node, .rejt-object-node > .rejt-collapsed, .rejt-array-node > .rejt-collapsed, .rejt-object-node > .rejt-not-collapsed, .rejt-array-node > .rejt-not-collapsed": {
    "& > svg": {
      opacity: 0,
      transition: "opacity 0.2s"
    }
  },
  ".rejt-value-node:hover, .rejt-object-node:hover > .rejt-collapsed, .rejt-array-node:hover > .rejt-collapsed, .rejt-object-node:hover > .rejt-not-collapsed, .rejt-array-node:hover > .rejt-not-collapsed": {
    "& > svg": {
      opacity: 1
    }
  },
  ".rejt-edit-form button": {
    display: "none"
  },
  ".rejt-add-form": {
    marginLeft: 10
  },
  ".rejt-add-value-node": {
    display: "inline-flex",
    alignItems: "center"
  },
  ".rejt-name": {
    lineHeight: "22px"
  },
  ".rejt-not-collapsed-delimiter": {
    lineHeight: "22px"
  },
  ".rejt-plus-menu": {
    marginLeft: 5
  },
  ".rejt-object-node > span > *, .rejt-array-node > span > *": {
    position: "relative",
    zIndex: 2
  },
  ".rejt-object-node, .rejt-array-node": {
    position: "relative"
  },
  ".rejt-object-node > span:first-of-type::after, .rejt-array-node > span:first-of-type::after, .rejt-collapsed::before, .rejt-not-collapsed::before": {
    content: '""',
    position: "absolute",
    top: 0,
    display: "block",
    width: "100%",
    marginLeft: "-1rem",
    padding: "0 4px 0 1rem",
    height: 22
  },
  ".rejt-collapsed::before, .rejt-not-collapsed::before": {
    zIndex: 1,
    background: "transparent",
    borderRadius: 4,
    transition: "background 0.2s",
    pointerEvents: "none",
    opacity: 0.1
  },
  ".rejt-object-node:hover, .rejt-array-node:hover": {
    "& > .rejt-collapsed::before, & > .rejt-not-collapsed::before": {
      background: theme.color.secondary
    }
  },
  ".rejt-collapsed::after, .rejt-not-collapsed::after": {
    content: '""',
    position: "absolute",
    display: "inline-block",
    pointerEvents: "none",
    width: 0,
    height: 0
  },
  ".rejt-collapsed::after": {
    left: -8,
    top: 8,
    borderTop: "3px solid transparent",
    borderBottom: "3px solid transparent",
    borderLeft: "3px solid rgba(153,153,153,0.6)"
  },
  ".rejt-not-collapsed::after": {
    left: -10,
    top: 10,
    borderTop: "3px solid rgba(153,153,153,0.6)",
    borderLeft: "3px solid transparent",
    borderRight: "3px solid transparent"
  },
  ".rejt-value": {
    display: "inline-block",
    border: "1px solid transparent",
    borderRadius: 4,
    margin: "1px 0",
    padding: "0 4px",
    cursor: "text",
    color: theme.color.defaultText
  },
  ".rejt-value-node:hover > .rejt-value": {
    background: theme.color.lighter,
    borderColor: theme.appBorderColor
  }
}));
var Button = styled.button(({ theme, primary }) => ({
  border: 0,
  height: 20,
  margin: 1,
  borderRadius: 4,
  background: primary ? theme.color.secondary : "transparent",
  color: primary ? theme.color.lightest : theme.color.dark,
  fontWeight: primary ? "bold" : "normal",
  cursor: "pointer",
  order: primary ? "initial" : 9
}));
var ActionIcon = styled(Icons)(({ theme, icon, disabled }) => ({
  display: "inline-block",
  verticalAlign: "middle",
  width: 15,
  height: 15,
  padding: 3,
  marginLeft: 5,
  cursor: disabled ? "not-allowed" : "pointer",
  color: theme.textMutedColor,
  "&:hover": disabled ? {} : {
    color: icon === "subtract" ? theme.color.negative : theme.color.ancillary
  },
  "svg + &": {
    marginLeft: 0
  }
}));
var Input = styled.input(({ theme, placeholder }) => ({
  outline: 0,
  margin: placeholder ? 1 : "1px 0",
  padding: "3px 4px",
  color: theme.color.defaultText,
  background: theme.background.app,
  border: `1px solid ${theme.appBorderColor}`,
  borderRadius: 4,
  lineHeight: "14px",
  width: placeholder === "Key" ? 80 : 120,
  "&:focus": {
    border: `1px solid ${theme.color.secondary}`
  }
}));
var RawButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  zIndex: 2,
  top: 2,
  right: 2,
  height: 21,
  padding: "0 3px",
  background: theme.background.bar,
  border: `1px solid ${theme.appBorderColor}`,
  borderRadius: 3,
  color: theme.textMutedColor,
  fontSize: "9px",
  fontWeight: "bold",
  textDecoration: "none",
  span: {
    marginLeft: 3,
    marginTop: 1
  }
}));
var RawInput = styled(Form.Textarea)(({ theme }) => ({
  flex: 1,
  padding: "7px 6px",
  fontFamily: theme.typography.fonts.mono,
  fontSize: "12px",
  lineHeight: "18px",
  "&::placeholder": {
    fontFamily: theme.typography.fonts.base,
    fontSize: "13px"
  },
  "&:placeholder-shown": {
    padding: "7px 10px"
  }
}));
var ENTER_EVENT = { bubbles: true, cancelable: true, key: "Enter", code: "Enter", keyCode: 13 };
var dispatchEnterKey = (event) => {
  event.currentTarget.dispatchEvent(new globalWindow2.KeyboardEvent("keydown", ENTER_EVENT));
};
var selectValue = (event) => {
  event.currentTarget.select();
};
var getCustomStyleFunction = (theme) => () => ({
  name: {
    color: theme.color.secondary
  },
  collapsed: {
    color: theme.color.dark
  },
  ul: {
    listStyle: "none",
    margin: "0 0 0 1rem",
    padding: 0
  },
  li: {
    outline: 0
  }
});
var ObjectControl = ({ name, value: value2, onChange }) => {
  const theme = useTheme();
  const data = useMemo(() => value2 && cloneDeep(value2), [value2]);
  const hasData = data !== null && data !== void 0;
  const [showRaw, setShowRaw] = useState(!hasData);
  const [parseError, setParseError] = useState(null);
  const updateRaw = useCallback(
    (raw) => {
      try {
        if (raw)
          onChange(JSON.parse(raw));
        setParseError(void 0);
      } catch (e) {
        setParseError(e);
      }
    },
    [onChange]
  );
  const [forceVisible, setForceVisible] = useState(false);
  const onForceVisible = useCallback(() => {
    onChange({});
    setForceVisible(true);
  }, [setForceVisible]);
  const htmlElRef = useRef(null);
  useEffect(() => {
    if (forceVisible && htmlElRef.current)
      htmlElRef.current.select();
  }, [forceVisible]);
  if (!hasData) {
    return /* @__PURE__ */ React17.createElement(Form.Button, { id: getControlSetterButtonId(name), onClick: onForceVisible }, "Set object");
  }
  const rawJSONForm = /* @__PURE__ */ React17.createElement(
    RawInput,
    {
      ref: htmlElRef,
      id: getControlId(name),
      name,
      defaultValue: value2 === null ? "" : JSON.stringify(value2, null, 2),
      onBlur: (event) => updateRaw(event.target.value),
      placeholder: "Edit JSON string...",
      autoFocus: forceVisible,
      valid: parseError ? "error" : null
    }
  );
  return /* @__PURE__ */ React17.createElement(Wrapper5, null, ["Object", "Array"].includes(getObjectType(data)) && /* @__PURE__ */ React17.createElement(
    RawButton,
    {
      href: "#",
      onClick: (e) => {
        e.preventDefault();
        setShowRaw((v) => !v);
      }
    },
    showRaw ? /* @__PURE__ */ React17.createElement(Icon.EyeClose, null) : /* @__PURE__ */ React17.createElement(Icon.Eye, null),
    /* @__PURE__ */ React17.createElement("span", null, "RAW")
  ), !showRaw ? /* @__PURE__ */ React17.createElement(
    JsonTree,
    {
      data,
      rootName: name,
      onFullyUpdate: onChange,
      getStyle: getCustomStyleFunction(theme),
      cancelButtonElement: /* @__PURE__ */ React17.createElement(Button, { type: "button" }, "Cancel"),
      editButtonElement: /* @__PURE__ */ React17.createElement(Button, { type: "submit" }, "Save"),
      addButtonElement: /* @__PURE__ */ React17.createElement(Button, { type: "submit", primary: true }, "Save"),
      plusMenuElement: /* @__PURE__ */ React17.createElement(ActionIcon, { icon: "add" }),
      minusMenuElement: /* @__PURE__ */ React17.createElement(ActionIcon, { icon: "subtract" }),
      inputElement: (_, __, ___, key) => key ? /* @__PURE__ */ React17.createElement(Input, { onFocus: selectValue, onBlur: dispatchEnterKey }) : /* @__PURE__ */ React17.createElement(Input, null),
      fallback: rawJSONForm
    }
  ) : rawJSONForm);
};
var RangeInput = styled.input(
  ({ theme, min, max, value: value2 }) => ({
    // Resytled using http://danielstern.ca/range.css/#/
    "&": {
      width: "100%",
      backgroundColor: "transparent",
      appearance: "none"
    },
    "&::-webkit-slider-runnable-track": {
      background: theme.base === "light" ? `linear-gradient(to right, 
            ${theme.color.green} 0%, ${theme.color.green} ${(value2 - min) / (max - min) * 100}%, 
            ${darken(0.02, theme.input.background)} ${(value2 - min) / (max - min) * 100}%, 
            ${darken(0.02, theme.input.background)} 100%)` : `linear-gradient(to right, 
            ${theme.color.green} 0%, ${theme.color.green} ${(value2 - min) / (max - min) * 100}%, 
            ${lighten(0.02, theme.input.background)} ${(value2 - min) / (max - min) * 100}%, 
            ${lighten(0.02, theme.input.background)} 100%)`,
      boxShadow: `${theme.appBorderColor} 0 0 0 1px inset`,
      borderRadius: 6,
      width: "100%",
      height: 6,
      cursor: "pointer"
    },
    "&::-webkit-slider-thumb": {
      marginTop: "-6px",
      width: 16,
      height: 16,
      border: `1px solid ${rgba(theme.appBorderColor, 0.2)}`,
      borderRadius: "50px",
      boxShadow: `0 1px 3px 0px ${rgba(theme.appBorderColor, 0.2)}`,
      cursor: "grab",
      appearance: "none",
      background: `${theme.input.background}`,
      transition: "all 150ms ease-out",
      "&:hover": {
        background: `${darken(0.05, theme.input.background)}`,
        transform: "scale3d(1.1, 1.1, 1.1) translateY(-1px)",
        transition: "all 50ms ease-out"
      },
      "&:active": {
        background: `${theme.input.background}`,
        transform: "scale3d(1, 1, 1) translateY(0px)",
        cursor: "grabbing"
      }
    },
    "&:focus": {
      outline: "none",
      "&::-webkit-slider-runnable-track": {
        borderColor: rgba(theme.color.secondary, 0.4)
      },
      "&::-webkit-slider-thumb": {
        borderColor: theme.color.secondary,
        boxShadow: `0 0px 5px 0px ${theme.color.secondary}`
      }
    },
    "&::-moz-range-track": {
      background: theme.base === "light" ? `linear-gradient(to right, 
            ${theme.color.green} 0%, ${theme.color.green} ${(value2 - min) / (max - min) * 100}%, 
            ${darken(0.02, theme.input.background)} ${(value2 - min) / (max - min) * 100}%, 
            ${darken(0.02, theme.input.background)} 100%)` : `linear-gradient(to right, 
            ${theme.color.green} 0%, ${theme.color.green} ${(value2 - min) / (max - min) * 100}%, 
            ${lighten(0.02, theme.input.background)} ${(value2 - min) / (max - min) * 100}%, 
            ${lighten(0.02, theme.input.background)} 100%)`,
      boxShadow: `${theme.appBorderColor} 0 0 0 1px inset`,
      borderRadius: 6,
      width: "100%",
      height: 6,
      cursor: "pointer",
      outline: "none"
    },
    "&::-moz-range-thumb": {
      width: 16,
      height: 16,
      border: `1px solid ${rgba(theme.appBorderColor, 0.2)}`,
      borderRadius: "50px",
      boxShadow: `0 1px 3px 0px ${rgba(theme.appBorderColor, 0.2)}`,
      cursor: "grab",
      background: `${theme.input.background}`,
      transition: "all 150ms ease-out",
      "&:hover": {
        background: `${darken(0.05, theme.input.background)}`,
        transform: "scale3d(1.1, 1.1, 1.1) translateY(-1px)",
        transition: "all 50ms ease-out"
      },
      "&:active": {
        background: `${theme.input.background}`,
        transform: "scale3d(1, 1, 1) translateY(0px)",
        cursor: "grabbing"
      }
    },
    "&::-ms-track": {
      background: theme.base === "light" ? `linear-gradient(to right, 
            ${theme.color.green} 0%, ${theme.color.green} ${(value2 - min) / (max - min) * 100}%, 
            ${darken(0.02, theme.input.background)} ${(value2 - min) / (max - min) * 100}%, 
            ${darken(0.02, theme.input.background)} 100%)` : `linear-gradient(to right, 
            ${theme.color.green} 0%, ${theme.color.green} ${(value2 - min) / (max - min) * 100}%, 
            ${lighten(0.02, theme.input.background)} ${(value2 - min) / (max - min) * 100}%, 
            ${lighten(0.02, theme.input.background)} 100%)`,
      boxShadow: `${theme.appBorderColor} 0 0 0 1px inset`,
      color: "transparent",
      width: "100%",
      height: "6px",
      cursor: "pointer"
    },
    "&::-ms-fill-lower": {
      borderRadius: 6
    },
    "&::-ms-fill-upper": {
      borderRadius: 6
    },
    "&::-ms-thumb": {
      width: 16,
      height: 16,
      background: `${theme.input.background}`,
      border: `1px solid ${rgba(theme.appBorderColor, 0.2)}`,
      borderRadius: 50,
      cursor: "grab",
      marginTop: 0
    },
    "@supports (-ms-ime-align:auto)": { "input[type=range]": { margin: "0" } }
  })
);
var RangeLabel = styled.span({
  paddingLeft: 5,
  paddingRight: 5,
  fontSize: 12,
  whiteSpace: "nowrap",
  fontFeatureSettings: "tnum",
  fontVariantNumeric: "tabular-nums"
});
var RangeCurrentAndMaxLabel = styled(RangeLabel)(({ numberOFDecimalsPlaces, max }) => ({
  // Fixed width of "current / max" label to avoid slider width changes
  // 3 = size of separator " / "
  width: `${numberOFDecimalsPlaces + max.toString().length * 2 + 3}ch`,
  textAlign: "right",
  flexShrink: 0
}));
var RangeWrapper = styled.div({
  display: "flex",
  alignItems: "center",
  width: "100%"
});
function getNumberOfDecimalPlaces(number) {
  const match = number.toString().match(/(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/);
  return !match ? 0 : Math.max(
    0,
    // Number of digits right of decimal point.
    (match[1] ? match[1].length : 0) - // Adjust for scientific notation.
    (match[2] ? +match[2] : 0)
  );
}
var RangeControl = ({
  name,
  value: value2,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  onBlur,
  onFocus
}) => {
  const handleChange = (event) => {
    onChange(parse2(event.target.value));
  };
  const hasValue = value2 !== void 0;
  const numberOFDecimalsPlaces = useMemo(() => getNumberOfDecimalPlaces(step), [step]);
  return /* @__PURE__ */ React17.createElement(RangeWrapper, null, /* @__PURE__ */ React17.createElement(RangeLabel, null, min), /* @__PURE__ */ React17.createElement(
    RangeInput,
    {
      id: getControlId(name),
      type: "range",
      onChange: handleChange,
      ...{ name, value: value2, min, max, step, onFocus, onBlur }
    }
  ), /* @__PURE__ */ React17.createElement(RangeCurrentAndMaxLabel, { numberOFDecimalsPlaces, max }, hasValue ? value2.toFixed(numberOFDecimalsPlaces) : "--", " / ", max));
};
var Wrapper6 = styled.label({
  display: "flex"
});
var MaxLength = styled.div(({ isMaxed }) => ({
  marginLeft: "0.75rem",
  paddingTop: "0.35rem",
  color: isMaxed ? "red" : void 0
}));
var TextControl = ({
  name,
  value: value2,
  onChange,
  onFocus,
  onBlur,
  maxLength
}) => {
  const handleChange = (event) => {
    onChange(event.target.value);
  };
  const [forceVisible, setForceVisible] = useState(false);
  const onForceVisible = useCallback(() => {
    onChange("");
    setForceVisible(true);
  }, [setForceVisible]);
  if (value2 === void 0) {
    return /* @__PURE__ */ React17.createElement(Form.Button, { id: getControlSetterButtonId(name), onClick: onForceVisible }, "Set string");
  }
  const isValid = typeof value2 === "string";
  return /* @__PURE__ */ React17.createElement(Wrapper6, null, /* @__PURE__ */ React17.createElement(
    Form.Textarea,
    {
      id: getControlId(name),
      maxLength,
      onChange: handleChange,
      size: "flex",
      placeholder: "Edit string...",
      autoFocus: forceVisible,
      valid: isValid ? null : "error",
      ...{ name, value: isValid ? value2 : "", onFocus, onBlur }
    }
  ), maxLength && /* @__PURE__ */ React17.createElement(MaxLength, { isMaxed: value2?.length === maxLength }, value2?.length ?? 0, " / ", maxLength));
};
var FileInput = styled(Form.Input)({
  padding: 10
});
function revokeOldUrls(urls) {
  urls.forEach((url) => {
    if (url.startsWith("blob:")) {
      URL.revokeObjectURL(url);
    }
  });
}
var FilesControl = ({
  onChange,
  name,
  accept = "image/*",
  value: value2
}) => {
  const inputElement = useRef(null);
  function handleFileChange(e) {
    if (!e.target.files) {
      return;
    }
    const fileUrls = Array.from(e.target.files).map((file) => URL.createObjectURL(file));
    onChange(fileUrls);
    revokeOldUrls(value2);
  }
  useEffect(() => {
    if (value2 == null && inputElement.current) {
      inputElement.current.value = null;
    }
  }, [value2, name]);
  return /* @__PURE__ */ React17.createElement(
    FileInput,
    {
      ref: inputElement,
      id: getControlId(name),
      type: "file",
      name,
      multiple: true,
      onChange: handleFileChange,
      accept,
      size: "flex"
    }
  );
};

// src/controls/index.tsx
var LazyColorControl = lazy(() => import('./Color-7GWY2BML.mjs'));
var ColorControl = (props) => /* @__PURE__ */ React17.createElement(Suspense, { fallback: /* @__PURE__ */ React17.createElement("div", null) }, /* @__PURE__ */ React17.createElement(LazyColorControl, { ...props }));

// src/components/ArgsTable/ArgControl.tsx
var Controls2 = {
  array: ObjectControl,
  object: ObjectControl,
  boolean: BooleanControl,
  color: ColorControl,
  date: DateControl,
  number: NumberControl,
  check: OptionsControl,
  "inline-check": OptionsControl,
  radio: OptionsControl,
  "inline-radio": OptionsControl,
  select: OptionsControl,
  "multi-select": OptionsControl,
  range: RangeControl,
  text: TextControl,
  file: FilesControl
};
var NoControl = () => /* @__PURE__ */ React17.createElement(React17.Fragment, null, "-");
var ArgControl = ({ row, arg, updateArgs, isHovered }) => {
  const { key, control } = row;
  const [isFocused, setFocused] = useState(false);
  const [boxedValue, setBoxedValue] = useState({ value: arg });
  useEffect(() => {
    if (!isFocused)
      setBoxedValue({ value: arg });
  }, [isFocused, arg]);
  const onChange = useCallback(
    (argVal) => {
      setBoxedValue({ value: argVal });
      updateArgs({ [key]: argVal });
      return argVal;
    },
    [updateArgs, key]
  );
  const onBlur = useCallback(() => setFocused(false), []);
  const onFocus = useCallback(() => setFocused(true), []);
  if (!control || control.disable)
    return isHovered ? /* @__PURE__ */ React17.createElement(
      Link,
      {
        href: "https://storybook.js.org/docs/react/essentials/controls",
        target: "_blank",
        withArrow: true
      },
      "Setup controls"
    ) : /* @__PURE__ */ React17.createElement(NoControl, null);
  const props = { name: key, argType: row, value: boxedValue.value, onChange, onBlur, onFocus };
  const Control = Controls2[control.type] || NoControl;
  return /* @__PURE__ */ React17.createElement(Control, { ...props, ...control, controlType: control.type });
};

// src/components/ArgsTable/ArgRow.tsx
var Name = styled.span({ fontWeight: "bold" });
var Required = styled.span(({ theme }) => ({
  color: theme.color.negative,
  fontFamily: theme.typography.fonts.mono,
  cursor: "help"
}));
var Description = styled.div(({ theme }) => ({
  "&&": {
    p: {
      margin: "0 0 10px 0"
    },
    a: {
      color: theme.color.secondary
    }
  },
  code: {
    ...codeCommon({ theme }),
    fontSize: 12,
    fontFamily: theme.typography.fonts.mono
  },
  "& code": {
    margin: 0,
    display: "inline-block"
  },
  "& pre > code": {
    whiteSpace: "pre-wrap"
  }
}));
var Type = styled.div(({ theme, hasDescription }) => ({
  color: theme.base === "light" ? transparentize(0.1, theme.color.defaultText) : transparentize(0.2, theme.color.defaultText),
  marginTop: hasDescription ? 4 : 0
}));
var TypeWithJsDoc = styled.div(({ theme, hasDescription }) => ({
  color: theme.base === "light" ? transparentize(0.1, theme.color.defaultText) : transparentize(0.2, theme.color.defaultText),
  marginTop: hasDescription ? 12 : 0,
  marginBottom: 12
}));
var StyledTd = styled.td(({ theme, expandable }) => ({
  paddingLeft: expandable ? "40px !important" : "20px !important"
}));
var ArgRow = (props) => {
  const [isHovered, setIsHovered] = useState(false);
  const { row, updateArgs, compact, expandable, initialExpandedArgs } = props;
  const { name, description } = row;
  const table = row.table || {};
  const type = table.type || row.type;
  const defaultValue = table.defaultValue || row.defaultValue;
  const required = row.type?.required;
  const hasDescription = description != null && description !== "";
  return /* @__PURE__ */ React17.createElement("tr", { onMouseEnter: () => setIsHovered(true), onMouseLeave: () => setIsHovered(false) }, /* @__PURE__ */ React17.createElement(StyledTd, { expandable }, /* @__PURE__ */ React17.createElement(Name, null, name), required ? /* @__PURE__ */ React17.createElement(Required, { title: "Required" }, "*") : null), compact ? null : /* @__PURE__ */ React17.createElement("td", null, hasDescription && /* @__PURE__ */ React17.createElement(Description, null, /* @__PURE__ */ React17.createElement(Markdown, null, description)), table.jsDocTags != null ? /* @__PURE__ */ React17.createElement(React17.Fragment, null, /* @__PURE__ */ React17.createElement(TypeWithJsDoc, { hasDescription }, /* @__PURE__ */ React17.createElement(ArgValue, { value: type, initialExpandedArgs })), /* @__PURE__ */ React17.createElement(ArgJsDoc, { tags: table.jsDocTags })) : /* @__PURE__ */ React17.createElement(Type, { hasDescription }, /* @__PURE__ */ React17.createElement(ArgValue, { value: type, initialExpandedArgs }))), compact ? null : /* @__PURE__ */ React17.createElement("td", null, /* @__PURE__ */ React17.createElement(ArgValue, { value: defaultValue, initialExpandedArgs })), updateArgs ? /* @__PURE__ */ React17.createElement("td", null, /* @__PURE__ */ React17.createElement(ArgControl, { ...props, isHovered })) : null);
};
var ExpanderIcon = styled(Icons)(({ theme }) => ({
  marginRight: 8,
  marginLeft: -10,
  marginTop: -2,
  // optical alignment
  height: 12,
  width: 12,
  color: theme.base === "light" ? transparentize(0.25, theme.color.defaultText) : transparentize(0.3, theme.color.defaultText),
  border: "none",
  display: "inline-block"
}));
var FlexWrapper = styled.span(({ theme }) => ({
  display: "flex",
  lineHeight: "20px",
  alignItems: "center"
}));
var Section = styled.td(({ theme }) => ({
  position: "relative",
  letterSpacing: "0.35em",
  textTransform: "uppercase",
  fontWeight: theme.typography.weight.bold,
  fontSize: theme.typography.size.s1 - 1,
  color: theme.base === "light" ? transparentize(0.4, theme.color.defaultText) : transparentize(0.6, theme.color.defaultText),
  background: `${theme.background.app} !important`,
  "& ~ td": {
    background: `${theme.background.app} !important`
  }
}));
var Subsection = styled.td(({ theme }) => ({
  position: "relative",
  fontWeight: theme.typography.weight.bold,
  fontSize: theme.typography.size.s2 - 1,
  background: theme.background.app
}));
var StyledTd2 = styled.td(() => ({
  position: "relative"
}));
var StyledTr = styled.tr(({ theme }) => ({
  "&:hover > td": {
    backgroundColor: `${lighten(5e-3, theme.background.app)} !important`,
    boxShadow: `${theme.color.mediumlight} 0 - 1px 0 0 inset`,
    cursor: "row-resize"
  }
}));
var ClickIntercept = styled.button(() => ({
  // reset button style
  background: "none",
  border: "none",
  padding: "0",
  font: "inherit",
  // add custom style
  position: "absolute",
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  height: "100%",
  width: "100%",
  color: "transparent",
  cursor: "row-resize !important"
}));
var SectionRow = ({
  level = "section",
  label,
  children,
  initialExpanded = true,
  colSpan = 3
}) => {
  const [expanded, setExpanded] = useState(initialExpanded);
  const Level = level === "subsection" ? Subsection : Section;
  const itemCount = children?.length || 0;
  const caption = level === "subsection" ? `${itemCount} item${itemCount !== 1 ? "s" : ""}` : "";
  const icon = expanded ? "arrowdown" : "arrowright";
  const helperText = `${expanded ? "Hide" : "Show"} ${level === "subsection" ? itemCount : label} item${itemCount !== 1 ? "s" : ""}`;
  return /* @__PURE__ */ React17.createElement(React17.Fragment, null, /* @__PURE__ */ React17.createElement(StyledTr, { title: helperText }, /* @__PURE__ */ React17.createElement(Level, { colSpan: 1 }, /* @__PURE__ */ React17.createElement(ClickIntercept, { onClick: (e) => setExpanded(!expanded), tabIndex: 0 }, helperText), /* @__PURE__ */ React17.createElement(FlexWrapper, null, /* @__PURE__ */ React17.createElement(ExpanderIcon, { icon }), label)), /* @__PURE__ */ React17.createElement(StyledTd2, { colSpan: colSpan - 1 }, /* @__PURE__ */ React17.createElement(
    ClickIntercept,
    {
      onClick: (e) => setExpanded(!expanded),
      tabIndex: -1,
      style: { outline: "none" }
    },
    helperText
  ), expanded ? null : caption)), expanded ? children : null);
};
var Row = styled.div(({ theme }) => ({
  display: "flex",
  gap: 16,
  borderBottom: `1px solid ${theme.appBorderColor}`,
  "&:last-child": {
    borderBottom: 0
  }
}));
var Column = styled.div(({ numColumn }) => ({
  display: "flex",
  flexDirection: "column",
  flex: numColumn || 1,
  gap: 5,
  padding: "12px 20px"
}));
var SkeletonText = styled.div(
  ({ theme, width, height }) => ({
    animation: `${theme.animation.glow} 1.5s ease-in-out infinite`,
    background: theme.appBorderColor,
    width: width || "100%",
    height: height || 16,
    borderRadius: 3
  })
);
var columnWidth = [2, 4, 2, 2];
var Skeleton = () => /* @__PURE__ */ React17.createElement(React17.Fragment, null, /* @__PURE__ */ React17.createElement(Row, null, /* @__PURE__ */ React17.createElement(Column, { numColumn: columnWidth[0] }, /* @__PURE__ */ React17.createElement(SkeletonText, { width: "60%" })), /* @__PURE__ */ React17.createElement(Column, { numColumn: columnWidth[1] }, /* @__PURE__ */ React17.createElement(SkeletonText, { width: "30%" })), /* @__PURE__ */ React17.createElement(Column, { numColumn: columnWidth[2] }, /* @__PURE__ */ React17.createElement(SkeletonText, { width: "60%" })), /* @__PURE__ */ React17.createElement(Column, { numColumn: columnWidth[3] }, /* @__PURE__ */ React17.createElement(SkeletonText, { width: "60%" }))), /* @__PURE__ */ React17.createElement(Row, null, /* @__PURE__ */ React17.createElement(Column, { numColumn: columnWidth[0] }, /* @__PURE__ */ React17.createElement(SkeletonText, { width: "60%" })), /* @__PURE__ */ React17.createElement(Column, { numColumn: columnWidth[1] }, /* @__PURE__ */ React17.createElement(SkeletonText, { width: "80%" }), /* @__PURE__ */ React17.createElement(SkeletonText, { width: "30%" })), /* @__PURE__ */ React17.createElement(Column, { numColumn: columnWidth[2] }, /* @__PURE__ */ React17.createElement(SkeletonText, { width: "60%" })), /* @__PURE__ */ React17.createElement(Column, { numColumn: columnWidth[3] }, /* @__PURE__ */ React17.createElement(SkeletonText, { width: "60%" }))), /* @__PURE__ */ React17.createElement(Row, null, /* @__PURE__ */ React17.createElement(Column, { numColumn: columnWidth[0] }, /* @__PURE__ */ React17.createElement(SkeletonText, { width: "60%" })), /* @__PURE__ */ React17.createElement(Column, { numColumn: columnWidth[1] }, /* @__PURE__ */ React17.createElement(SkeletonText, { width: "80%" }), /* @__PURE__ */ React17.createElement(SkeletonText, { width: "30%" })), /* @__PURE__ */ React17.createElement(Column, { numColumn: columnWidth[2] }, /* @__PURE__ */ React17.createElement(SkeletonText, { width: "60%" })), /* @__PURE__ */ React17.createElement(Column, { numColumn: columnWidth[3] }, /* @__PURE__ */ React17.createElement(SkeletonText, { width: "60%" }))), /* @__PURE__ */ React17.createElement(Row, null, /* @__PURE__ */ React17.createElement(Column, { numColumn: columnWidth[0] }, /* @__PURE__ */ React17.createElement(SkeletonText, { width: "60%" })), /* @__PURE__ */ React17.createElement(Column, { numColumn: columnWidth[1] }, /* @__PURE__ */ React17.createElement(SkeletonText, { width: "80%" }), /* @__PURE__ */ React17.createElement(SkeletonText, { width: "30%" })), /* @__PURE__ */ React17.createElement(Column, { numColumn: columnWidth[2] }, /* @__PURE__ */ React17.createElement(SkeletonText, { width: "60%" })), /* @__PURE__ */ React17.createElement(Column, { numColumn: columnWidth[3] }, /* @__PURE__ */ React17.createElement(SkeletonText, { width: "60%" }))));
var Wrapper7 = styled.div(({ inAddonPanel, theme }) => ({
  height: inAddonPanel ? "100%" : "auto",
  display: "flex",
  border: inAddonPanel ? "none" : `1px solid ${theme.appBorderColor}`,
  borderRadius: inAddonPanel ? 0 : theme.appBorderRadius,
  padding: inAddonPanel ? 0 : 40,
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  gap: 15,
  background: theme.background.content,
  boxShadow: "rgba(0, 0, 0, 0.10) 0 1px 3px 0"
}));
var Content = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: 4,
  maxWidth: 415
});
var Title2 = styled.div(({ theme }) => ({
  fontWeight: theme.typography.weight.bold,
  fontSize: theme.typography.size.s2 - 1,
  textAlign: "center",
  color: theme.textColor
}));
var Description2 = styled.div(({ theme }) => ({
  fontWeight: theme.typography.weight.regular,
  fontSize: theme.typography.size.s2 - 1,
  textAlign: "center",
  color: theme.textMutedColor
}));
var Links = styled.div({
  display: "flex",
  gap: 25
});
var Divider = styled.div(({ theme }) => ({
  width: 1,
  height: 16,
  backgroundColor: theme.appBorderColor
}));
var VideoIcon = styled.div(({ theme }) => ({
  width: 22,
  height: 16,
  borderRadius: theme.appBorderRadius,
  border: `1px solid ${theme.color.secondary}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
}));
var Empty = ({ inAddonPanel }) => {
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    const load = setTimeout(() => {
      setIsLoading(false);
    }, 100);
    return () => clearTimeout(load);
  }, []);
  if (isLoading)
    return null;
  return /* @__PURE__ */ React17.createElement(Wrapper7, { inAddonPanel }, /* @__PURE__ */ React17.createElement(Content, null, /* @__PURE__ */ React17.createElement(Title2, null, inAddonPanel ? "Interactive story playground" : "Args table with interactive controls couldn't be auto-generated"), /* @__PURE__ */ React17.createElement(Description2, null, "Controls give you an easy to use interface to test your components. Set your story args and you'll see controls appearing here automatically.")), /* @__PURE__ */ React17.createElement(Links, null, inAddonPanel && /* @__PURE__ */ React17.createElement(React17.Fragment, null, /* @__PURE__ */ React17.createElement(
    Link,
    {
      href: "https://youtu.be/0gOfS6K0x0E",
      target: "_blank",
      icon: /* @__PURE__ */ React17.createElement(VideoIcon, null, /* @__PURE__ */ React17.createElement(Icon.Play, { size: 8 })),
      withArrow: true
    },
    "Watch 5m video"
  ), /* @__PURE__ */ React17.createElement(Divider, null), /* @__PURE__ */ React17.createElement(
    Link,
    {
      href: "https://storybook.js.org/docs/react/essentials/controls",
      target: "_blank",
      withArrow: true
    },
    "Read docs"
  )), !inAddonPanel && /* @__PURE__ */ React17.createElement(
    Link,
    {
      href: "https://storybook.js.org/docs/react/essentials/controls",
      target: "_blank",
      withArrow: true
    },
    "Learn how to set that up"
  )));
};

// src/components/ArgsTable/ArgsTable.tsx
var TableWrapper = styled.table(({ theme, compact, inAddonPanel }) => ({
  "&&": {
    // Resets for cascading/system styles
    borderSpacing: 0,
    color: theme.color.defaultText,
    "td, th": {
      padding: 0,
      border: "none",
      verticalAlign: "top",
      textOverflow: "ellipsis"
    },
    // End Resets
    fontSize: theme.typography.size.s2 - 1,
    lineHeight: "20px",
    textAlign: "left",
    width: "100%",
    // Margin collapse
    marginTop: inAddonPanel ? 0 : 25,
    marginBottom: inAddonPanel ? 0 : 40,
    "thead th:first-of-type, td:first-of-type": {
      // intentionally specify thead here
      width: "25%"
    },
    "th:first-of-type, td:first-of-type": {
      paddingLeft: 20
    },
    "th:nth-of-type(2), td:nth-of-type(2)": {
      ...compact ? null : {
        // Description column
        width: "35%"
      }
    },
    "td:nth-of-type(3)": {
      ...compact ? null : {
        // Defaults column
        width: "15%"
      }
    },
    "th:last-of-type, td:last-of-type": {
      paddingRight: 20,
      ...compact ? null : {
        // Controls column
        width: "25%"
      }
    },
    th: {
      color: theme.base === "light" ? transparentize(0.25, theme.color.defaultText) : transparentize(0.45, theme.color.defaultText),
      paddingTop: 10,
      paddingBottom: 10,
      paddingLeft: 15,
      paddingRight: 15
    },
    td: {
      paddingTop: "10px",
      paddingBottom: "10px",
      "&:not(:first-of-type)": {
        paddingLeft: 15,
        paddingRight: 15
      },
      "&:last-of-type": {
        paddingRight: 20
      }
    },
    // Makes border alignment consistent w/other DocBlocks
    marginLeft: inAddonPanel ? 0 : 1,
    marginRight: inAddonPanel ? 0 : 1,
    tbody: {
      // Safari doesn't love shadows on tbody so we need to use a shadow filter. In order to do this,
      // the table cells all need to be solid so they have a background color applied.
      // I wasn't sure what kinds of content go in these tables so I was extra specific with selectors
      // to avoid unexpected surprises.
      ...inAddonPanel ? null : {
        filter: theme.base === "light" ? `drop-shadow(0px 1px 3px rgba(0, 0, 0, 0.10))` : `drop-shadow(0px 1px 3px rgba(0, 0, 0, 0.20))`
      },
      "> tr > *": {
        // For filter to work properly, the table cells all need to be opaque.
        background: theme.background.content,
        borderTop: `1px solid ${theme.appBorderColor}`
      },
      ...inAddonPanel ? null : {
        // This works and I don't know why. :)
        "> tr:first-of-type > *": {
          borderBlockStart: `1px solid ${theme.appBorderColor}`
        },
        "> tr:last-of-type > *": {
          borderBlockEnd: `1px solid ${theme.appBorderColor}`
        },
        "> tr > *:first-of-type": {
          borderInlineStart: `1px solid ${theme.appBorderColor}`
        },
        "> tr > *:last-of-type": {
          borderInlineEnd: `1px solid ${theme.appBorderColor}`
        },
        // Thank you, Safari, for making me write code like this.
        "> tr:first-of-type > td:first-of-type": {
          borderTopLeftRadius: theme.appBorderRadius
        },
        "> tr:first-of-type > td:last-of-type": {
          borderTopRightRadius: theme.appBorderRadius
        },
        "> tr:last-of-type > td:first-of-type": {
          borderBottomLeftRadius: theme.appBorderRadius
        },
        "> tr:last-of-type > td:last-of-type": {
          borderBottomRightRadius: theme.appBorderRadius
        }
      }
    }
    // End awesome table styling
  }
}));
var StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.barTextColor,
  margin: "-4px -12px -4px 0"
}));
var ControlHeadingWrapper = styled.span({
  display: "flex",
  justifyContent: "space-between"
});
var sortFns = {
  alpha: (a, b) => a.name.localeCompare(b.name),
  requiredFirst: (a, b) => Number(!!b.type?.required) - Number(!!a.type?.required) || a.name.localeCompare(b.name),
  none: void 0
};
var groupRows = (rows, sort) => {
  const sections = { ungrouped: [], ungroupedSubsections: {}, sections: {} };
  if (!rows)
    return sections;
  Object.entries(rows).forEach(([key, row]) => {
    const { category, subcategory } = row?.table || {};
    if (category) {
      const section = sections.sections[category] || { ungrouped: [], subsections: {} };
      if (!subcategory) {
        section.ungrouped.push({ key, ...row });
      } else {
        const subsection = section.subsections[subcategory] || [];
        subsection.push({ key, ...row });
        section.subsections[subcategory] = subsection;
      }
      sections.sections[category] = section;
    } else if (subcategory) {
      const subsection = sections.ungroupedSubsections[subcategory] || [];
      subsection.push({ key, ...row });
      sections.ungroupedSubsections[subcategory] = subsection;
    } else {
      sections.ungrouped.push({ key, ...row });
    }
  });
  const sortFn = sortFns[sort];
  const sortSubsection = (record) => {
    if (!sortFn)
      return record;
    return Object.keys(record).reduce(
      (acc, cur) => ({
        ...acc,
        [cur]: record[cur].sort(sortFn)
      }),
      {}
    );
  };
  const sorted = {
    ungrouped: sections.ungrouped.sort(sortFn),
    ungroupedSubsections: sortSubsection(sections.ungroupedSubsections),
    sections: Object.keys(sections.sections).reduce(
      (acc, cur) => ({
        ...acc,
        [cur]: {
          ungrouped: sections.sections[cur].ungrouped.sort(sortFn),
          subsections: sortSubsection(sections.sections[cur].subsections)
        }
      }),
      {}
    )
  };
  return sorted;
};
var safeIncludeConditionalArg = (row, args, globals) => {
  try {
    return includeConditionalArg(row, args, globals);
  } catch (err) {
    once.warn(err.message);
    return false;
  }
};
var ArgsTable = (props) => {
  const {
    updateArgs,
    resetArgs,
    compact,
    inAddonPanel,
    initialExpandedArgs,
    sort = "none",
    isLoading
  } = props;
  if ("error" in props) {
    const { error } = props;
    return /* @__PURE__ */ React17.createElement(EmptyBlock, null, error, "\xA0", /* @__PURE__ */ React17.createElement(Link, { href: "http://storybook.js.org/docs/", target: "_blank", withArrow: true }, "Read the docs"));
  }
  if (isLoading)
    return /* @__PURE__ */ React17.createElement(Skeleton, null);
  const { rows, args, globals } = "rows" in props && props;
  const groups = groupRows(
    pickBy(
      rows,
      (row) => !row?.table?.disable && safeIncludeConditionalArg(row, args || {}, globals || {})
    ),
    sort
  );
  const hasNoUngrouped = groups.ungrouped.length === 0;
  const hasNoSections = Object.entries(groups.sections).length === 0;
  const hasNoUngroupedSubsections = Object.entries(groups.ungroupedSubsections).length === 0;
  if (hasNoUngrouped && hasNoSections && hasNoUngroupedSubsections)
    return /* @__PURE__ */ React17.createElement(Empty, { inAddonPanel });
  let colSpan = 1;
  if (updateArgs)
    colSpan += 1;
  if (!compact)
    colSpan += 2;
  const expandable = Object.keys(groups.sections).length > 0;
  const common = { updateArgs, compact, inAddonPanel, initialExpandedArgs };
  return /* @__PURE__ */ React17.createElement(ResetWrapper, null, /* @__PURE__ */ React17.createElement(TableWrapper, { ...{ compact, inAddonPanel }, className: "docblock-argstable sb-unstyled" }, /* @__PURE__ */ React17.createElement("thead", { className: "docblock-argstable-head" }, /* @__PURE__ */ React17.createElement("tr", null, /* @__PURE__ */ React17.createElement("th", null, /* @__PURE__ */ React17.createElement("span", null, "Name")), compact ? null : /* @__PURE__ */ React17.createElement("th", null, /* @__PURE__ */ React17.createElement("span", null, "Description")), compact ? null : /* @__PURE__ */ React17.createElement("th", null, /* @__PURE__ */ React17.createElement("span", null, "Default")), updateArgs ? /* @__PURE__ */ React17.createElement("th", null, /* @__PURE__ */ React17.createElement(ControlHeadingWrapper, null, "Control", " ", !isLoading && resetArgs && /* @__PURE__ */ React17.createElement(StyledIconButton, { onClick: () => resetArgs(), title: "Reset controls" }, /* @__PURE__ */ React17.createElement(Icon.Undo, { "aria-hidden": true })))) : null)), /* @__PURE__ */ React17.createElement("tbody", { className: "docblock-argstable-body" }, groups.ungrouped.map((row) => /* @__PURE__ */ React17.createElement(ArgRow, { key: row.key, row, arg: args && args[row.key], ...common })), Object.entries(groups.ungroupedSubsections).map(([subcategory, subsection]) => /* @__PURE__ */ React17.createElement(SectionRow, { key: subcategory, label: subcategory, level: "subsection", colSpan }, subsection.map((row) => /* @__PURE__ */ React17.createElement(
    ArgRow,
    {
      key: row.key,
      row,
      arg: args && args[row.key],
      expandable,
      ...common
    }
  )))), Object.entries(groups.sections).map(([category, section]) => /* @__PURE__ */ React17.createElement(SectionRow, { key: category, label: category, level: "section", colSpan }, section.ungrouped.map((row) => /* @__PURE__ */ React17.createElement(ArgRow, { key: row.key, row, arg: args && args[row.key], ...common })), Object.entries(section.subsections).map(([subcategory, subsection]) => /* @__PURE__ */ React17.createElement(
    SectionRow,
    {
      key: subcategory,
      label: subcategory,
      level: "subsection",
      colSpan
    },
    subsection.map((row) => /* @__PURE__ */ React17.createElement(
      ArgRow,
      {
        key: row.key,
        row,
        arg: args && args[row.key],
        expandable,
        ...common
      }
    ))
  )))))));
};
var TabbedArgsTable = ({ tabs, ...props }) => {
  const entries = Object.entries(tabs);
  if (entries.length === 1) {
    return /* @__PURE__ */ React17.createElement(ArgsTable, { ...entries[0][1], ...props });
  }
  return /* @__PURE__ */ React17.createElement(TabsState, null, entries.map((entry) => {
    const [label, table] = entry;
    const id = `prop_table_div_${label}`;
    const Component4 = "div";
    return /* @__PURE__ */ React17.createElement(Component4, { key: id, id, title: label }, ({ active }) => active ? /* @__PURE__ */ React17.createElement(ArgsTable, { key: `prop_table_${label}`, ...table, ...props }) : null);
  }));
};
var Label4 = styled.div(({ theme }) => ({
  marginRight: 30,
  fontSize: `${theme.typography.size.s1}px`,
  color: theme.base === "light" ? transparentize(0.4, theme.color.defaultText) : transparentize(0.6, theme.color.defaultText)
}));
var Sample = styled.div({
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis"
});
var TypeSpecimen = styled.div({
  display: "flex",
  flexDirection: "row",
  alignItems: "baseline",
  "&:not(:last-child)": { marginBottom: "1rem" }
});
var Wrapper8 = styled.div(withReset, ({ theme }) => ({
  ...getBlockBackgroundStyle(theme),
  margin: "25px 0 40px",
  padding: "30px 20px"
}));
var Typeset = ({
  fontFamily,
  fontSizes,
  fontWeight,
  sampleText,
  ...props
}) => /* @__PURE__ */ React17.createElement(Wrapper8, { ...props, className: "docblock-typeset sb-unstyled" }, fontSizes.map((size) => /* @__PURE__ */ React17.createElement(TypeSpecimen, { key: size }, /* @__PURE__ */ React17.createElement(Label4, null, size), /* @__PURE__ */ React17.createElement(
  Sample,
  {
    style: {
      fontFamily,
      fontSize: size,
      fontWeight,
      lineHeight: 1.2
    }
  },
  sampleText || "Was he a beast if music could move him so?"
))));
var ItemTitle = styled.div(({ theme }) => ({
  fontWeight: theme.typography.weight.bold,
  color: theme.color.defaultText
}));
var ItemSubtitle = styled.div(({ theme }) => ({
  color: theme.base === "light" ? transparentize(0.2, theme.color.defaultText) : transparentize(0.6, theme.color.defaultText)
}));
var ItemDescription = styled.div({
  flex: "0 0 30%",
  lineHeight: "20px",
  marginTop: 5
});
var SwatchLabel = styled.div(({ theme }) => ({
  flex: 1,
  textAlign: "center",
  fontFamily: theme.typography.fonts.mono,
  fontSize: theme.typography.size.s1,
  lineHeight: 1,
  overflow: "hidden",
  color: theme.base === "light" ? transparentize(0.4, theme.color.defaultText) : transparentize(0.6, theme.color.defaultText),
  "> div": {
    display: "inline-block",
    overflow: "hidden",
    maxWidth: "100%",
    textOverflow: "ellipsis"
  },
  span: {
    display: "block",
    marginTop: 2
  }
}));
var SwatchLabels = styled.div({
  display: "flex",
  flexDirection: "row"
});
var Swatch = styled.div(({ background }) => ({
  position: "relative",
  flex: 1,
  "&::before": {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background,
    content: '""'
  }
}));
var SwatchColors = styled.div(({ theme }) => ({
  ...getBlockBackgroundStyle(theme),
  display: "flex",
  flexDirection: "row",
  height: 50,
  marginBottom: 5,
  overflow: "hidden",
  backgroundColor: "white",
  backgroundImage: `repeating-linear-gradient(-45deg, #ccc, #ccc 1px, #fff 1px, #fff 16px)`,
  backgroundClip: "padding-box"
}));
var SwatchSpecimen = styled.div({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  position: "relative",
  marginBottom: 30
});
var Swatches = styled.div({
  flex: 1,
  display: "flex",
  flexDirection: "row"
});
var Item = styled.div({
  display: "flex",
  alignItems: "flex-start"
});
var ListName = styled.div({
  flex: "0 0 30%"
});
var ListSwatches = styled.div({
  flex: 1
});
var ListHeading = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  paddingBottom: 20,
  fontWeight: theme.typography.weight.bold,
  color: theme.base === "light" ? transparentize(0.4, theme.color.defaultText) : transparentize(0.6, theme.color.defaultText)
}));
var List = styled.div(({ theme }) => ({
  fontSize: theme.typography.size.s2,
  lineHeight: `20px`,
  display: "flex",
  flexDirection: "column"
}));
function renderSwatch(color, index) {
  return /* @__PURE__ */ React17.createElement(Swatch, { key: `${color}-${index}`, title: color, background: color });
}
function renderSwatchLabel(color, index, colorDescription) {
  return /* @__PURE__ */ React17.createElement(SwatchLabel, { key: `${color}-${index}`, title: color }, /* @__PURE__ */ React17.createElement("div", null, color, colorDescription && /* @__PURE__ */ React17.createElement("span", null, colorDescription)));
}
function renderSwatchSpecimen(colors) {
  if (Array.isArray(colors)) {
    return /* @__PURE__ */ React17.createElement(SwatchSpecimen, null, /* @__PURE__ */ React17.createElement(SwatchColors, null, colors.map((color, index) => renderSwatch(color, index))), /* @__PURE__ */ React17.createElement(SwatchLabels, null, colors.map((color, index) => renderSwatchLabel(color, index))));
  }
  return /* @__PURE__ */ React17.createElement(SwatchSpecimen, null, /* @__PURE__ */ React17.createElement(SwatchColors, null, Object.values(colors).map((color, index) => renderSwatch(color, index))), /* @__PURE__ */ React17.createElement(SwatchLabels, null, Object.keys(colors).map((color, index) => renderSwatchLabel(color, index, colors[color]))));
}
var ColorItem = ({ title, subtitle, colors }) => {
  return /* @__PURE__ */ React17.createElement(Item, null, /* @__PURE__ */ React17.createElement(ItemDescription, null, /* @__PURE__ */ React17.createElement(ItemTitle, null, title), /* @__PURE__ */ React17.createElement(ItemSubtitle, null, subtitle)), /* @__PURE__ */ React17.createElement(Swatches, null, renderSwatchSpecimen(colors)));
};
var ColorPalette = ({ children, ...props }) => /* @__PURE__ */ React17.createElement(ResetWrapper, null, /* @__PURE__ */ React17.createElement(List, { ...props, className: "docblock-colorpalette sb-unstyled" }, /* @__PURE__ */ React17.createElement(ListHeading, null, /* @__PURE__ */ React17.createElement(ListName, null, "Name"), /* @__PURE__ */ React17.createElement(ListSwatches, null, "Swatches")), children));
var ItemLabel = styled.div(({ theme }) => ({
  fontFamily: theme.typography.fonts.base,
  fontSize: theme.typography.size.s2,
  color: theme.color.defaultText,
  marginLeft: 10,
  lineHeight: 1.2
}));
var ItemSpecimen = styled.div(({ theme }) => ({
  ...getBlockBackgroundStyle(theme),
  overflow: "hidden",
  height: 40,
  width: 40,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flex: "none",
  "> img, > svg": {
    width: 20,
    height: 20
  }
}));
var Item2 = styled.div({
  display: "inline-flex",
  flexDirection: "row",
  alignItems: "center",
  flex: "0 1 calc(20% - 10px)",
  minWidth: 120,
  margin: "0px 10px 30px 0"
});
var List2 = styled.div({
  display: "flex",
  flexFlow: "row wrap"
});
var IconItem = ({ name, children }) => /* @__PURE__ */ React17.createElement(Item2, null, /* @__PURE__ */ React17.createElement(ItemSpecimen, null, children), /* @__PURE__ */ React17.createElement(ItemLabel, null, name));
var IconGallery = ({ children, ...props }) => /* @__PURE__ */ React17.createElement(ResetWrapper, null, /* @__PURE__ */ React17.createElement(List2, { ...props, className: "docblock-icongallery sb-unstyled" }, children));
var anchorBlockIdFromId = (storyId) => `anchor--${storyId}`;
var Anchor = ({ storyId, children }) => /* @__PURE__ */ React17.createElement("div", { id: anchorBlockIdFromId(storyId), className: "sb-anchor" }, children);
if (global && global.__DOCS_CONTEXT__ === void 0) {
  global.__DOCS_CONTEXT__ = createContext(null);
  global.__DOCS_CONTEXT__.displayName = "DocsContext";
}
var DocsContext = global ? global.__DOCS_CONTEXT__ : createContext(null);

// src/blocks/useOf.ts
var useOf = (moduleExportOrType, validTypes) => {
  const context = useContext(DocsContext);
  return context.resolveOf(moduleExportOrType, validTypes);
};

// src/blocks/ArgTypes.tsx
function extractComponentArgTypes(component, parameters) {
  const { extractArgTypes } = parameters.docs || {};
  if (!extractArgTypes) {
    throw new Error("Args unsupported. See Args documentation for your framework." /* ARGS_UNSUPPORTED */);
  }
  return extractArgTypes(component);
}
function getArgTypesFromResolved(resolved, props) {
  if (resolved.type === "component") {
    const {
      component,
      projectAnnotations: { parameters: parameters2 }
    } = resolved;
    return {
      argTypes: extractComponentArgTypes(component, parameters2),
      parameters: parameters2
    };
  }
  if (resolved.type === "meta") {
    const {
      preparedMeta: { argTypes: argTypes2, parameters: parameters2 }
    } = resolved;
    return { argTypes: argTypes2, parameters: parameters2 };
  }
  const {
    story: { argTypes, parameters }
  } = resolved;
  return { argTypes, parameters };
}
var ArgTypes = (props) => {
  const { of } = props;
  if ("of" in props && of === void 0) {
    throw new Error("Unexpected `of={undefined}`, did you mistype a CSF file reference?");
  }
  const resolved = useOf(of || "meta");
  const { argTypes, parameters } = getArgTypesFromResolved(resolved);
  const argTypesParameters = parameters.docs?.argTypes || {};
  const include = props.include ?? argTypesParameters.include;
  const exclude = props.exclude ?? argTypesParameters.exclude;
  const sort = props.sort ?? argTypesParameters.sort;
  const filteredArgTypes = filterArgTypes(argTypes, include, exclude);
  return /* @__PURE__ */ React17.createElement(ArgsTable, { rows: filteredArgTypes, sort });
};

// src/blocks/types.ts
var PRIMARY_STORY = "^";

// src/blocks/utils.ts
var titleCase = (str2) => str2.split("-").map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join("");
var getComponentName = (component) => {
  if (!component) {
    return void 0;
  }
  if (typeof component === "string") {
    if (component.includes("-")) {
      return titleCase(component);
    }
    return component;
  }
  if (component.__docgenInfo && component.__docgenInfo.displayName) {
    return component.__docgenInfo.displayName;
  }
  return component.name;
};
function scrollToElement(element, block = "start") {
  element.scrollIntoView({
    behavior: "smooth",
    block,
    inline: "nearest"
  });
}
function useStory(storyId, context) {
  const stories = useStories([storyId], context);
  return stories && stories[0];
}
function useStories(storyIds, context) {
  const [storiesById, setStories] = useState({});
  useEffect(() => {
    Promise.all(
      storyIds.map(async (storyId) => {
        const story = await context.loadStory(storyId);
        setStories(
          (current) => current[storyId] === story ? current : { ...current, [storyId]: story }
        );
      })
    );
  });
  return storyIds.map((storyId) => {
    if (storiesById[storyId])
      return storiesById[storyId];
    try {
      return context.storyById(storyId);
    } catch (err) {
      return null;
    }
  });
}

// src/blocks/ArgsTable.tsx
var useArgs = (storyId, context) => {
  const storyContext = context.getStoryContext(context.storyById());
  const [args, setArgs] = useState(storyContext.args);
  useEffect(() => {
    const cb = (changed) => {
      if (changed.storyId === storyId) {
        setArgs(changed.args);
      }
    };
    context.channel.on(STORY_ARGS_UPDATED, cb);
    return () => context.channel.off(STORY_ARGS_UPDATED, cb);
  }, [storyId]);
  const updateArgs = useCallback(
    (updatedArgs) => context.channel.emit(UPDATE_STORY_ARGS, { storyId, updatedArgs }),
    [storyId]
  );
  const resetArgs = useCallback(
    (argNames) => context.channel.emit(RESET_STORY_ARGS, { storyId, argNames }),
    [storyId]
  );
  return [args, updateArgs, resetArgs];
};
var useGlobals = (context) => {
  const storyContext = context.getStoryContext(context.storyById());
  const [globals, setGlobals] = useState(storyContext.globals);
  useEffect(() => {
    const cb = (changed) => {
      setGlobals(changed.globals);
    };
    context.channel.on(GLOBALS_UPDATED, cb);
    return () => context.channel.off(GLOBALS_UPDATED, cb);
  }, []);
  return [globals];
};
var extractComponentArgTypes2 = (component, parameters, include, exclude) => {
  const { extractArgTypes } = parameters.docs || {};
  if (!extractArgTypes) {
    throw new Error("Args unsupported. See Args documentation for your framework." /* ARGS_UNSUPPORTED */);
  }
  let argTypes = extractArgTypes(component);
  argTypes = filterArgTypes(argTypes, include, exclude);
  return argTypes;
};
var isShortcut = (value2) => {
  return value2 && [PRIMARY_STORY].includes(value2);
};
var getComponent = (props = {}, component) => {
  const { of } = props;
  const { story } = props;
  if (isShortcut(of) || isShortcut(story)) {
    return component || null;
  }
  if (!of) {
    throw new Error("No component found." /* NO_COMPONENT */);
  }
  return of;
};
var addComponentTabs = (tabs, components2, parameters, include, exclude, sort) => ({
  ...tabs,
  ...mapValues(components2, (comp) => ({
    rows: extractComponentArgTypes2(comp, parameters, include, exclude),
    sort
  }))
});
var StoryTable = (props) => {
  const context = useContext(DocsContext);
  const {
    story: storyName,
    component,
    subcomponents,
    showComponent,
    include,
    exclude,
    sort
  } = props;
  try {
    let storyId;
    switch (storyName) {
      case PRIMARY_STORY: {
        const primaryStory = context.storyById();
        storyId = primaryStory.id;
        break;
      }
      default: {
        storyId = context.storyIdByName(storyName);
      }
    }
    const story = useStory(storyId, context);
    let [args, updateArgs, resetArgs] = useArgs(storyId, context);
    const [globals] = useGlobals(context);
    if (!story)
      return /* @__PURE__ */ React17.createElement(ArgsTable, { isLoading: true, updateArgs, resetArgs });
    const argTypes = filterArgTypes(story.argTypes, include, exclude);
    const mainLabel = getComponentName(component) || "Story";
    let tabs = { [mainLabel]: { rows: argTypes, args, globals, updateArgs, resetArgs } };
    const storyHasArgsWithControls = argTypes && Object.values(argTypes).find((v) => !!v?.control);
    if (!storyHasArgsWithControls) {
      updateArgs = null;
      resetArgs = null;
      tabs = {};
    }
    if (component && (!storyHasArgsWithControls || showComponent)) {
      tabs = addComponentTabs(tabs, { [mainLabel]: component }, story.parameters, include, exclude);
    }
    if (subcomponents) {
      if (Array.isArray(subcomponents)) {
        throw new Error(
          `Unexpected subcomponents array. Expected an object whose keys are tab labels and whose values are components.`
        );
      }
      tabs = addComponentTabs(tabs, subcomponents, story.parameters, include, exclude);
    }
    return /* @__PURE__ */ React17.createElement(TabbedArgsTable, { tabs, sort });
  } catch (err) {
    return /* @__PURE__ */ React17.createElement(ArgsTable, { error: err.message });
  }
};
var ComponentsTable = (props) => {
  const { components: components2, include, exclude, sort, parameters } = props;
  const tabs = addComponentTabs({}, components2, parameters, include, exclude);
  return /* @__PURE__ */ React17.createElement(TabbedArgsTable, { tabs, sort });
};
var ArgsTable2 = (props) => {
  deprecate(dedent2`The ArgsTable doc block is deprecated. Instead use the ArgTypes doc block for static tables or the Controls doc block for tables with controls.
    
  Please refer to the migration guide: https://github.com/storybookjs/storybook/blob/next/MIGRATION.md#argstable-block
  `);
  const context = useContext(DocsContext);
  let parameters;
  let component;
  let subcomponents;
  try {
    ({ parameters, component, subcomponents } = context.storyById());
  } catch (err) {
    const { of } = props;
    if ("of" in props && of === void 0) {
      throw new Error("Unexpected `of={undefined}`, did you mistype a CSF file reference?");
    }
    ({
      projectAnnotations: { parameters }
    } = context.resolveOf(of, ["component"]));
  }
  const { include, exclude, components: components2, sort: sortProp } = props;
  const { story: storyName } = props;
  const sort = sortProp || parameters.controls?.sort;
  const main = getComponent(props, component);
  if (storyName) {
    return /* @__PURE__ */ React17.createElement(StoryTable, { ...props, component: main, ...{ subcomponents, sort } });
  }
  if (!components2 && !subcomponents) {
    let mainProps;
    try {
      mainProps = { rows: extractComponentArgTypes2(main, parameters, include, exclude) };
    } catch (err) {
      mainProps = { error: err.message };
    }
    return /* @__PURE__ */ React17.createElement(ArgsTable, { ...mainProps, sort });
  }
  if (components2) {
    return /* @__PURE__ */ React17.createElement(ComponentsTable, { ...props, ...{ components: components2, sort, parameters } });
  }
  const mainLabel = getComponentName(main);
  return /* @__PURE__ */ React17.createElement(
    ComponentsTable,
    {
      ...props,
      components: { [mainLabel]: main, ...subcomponents },
      sort,
      parameters
    }
  );
};
ArgsTable2.defaultProps = {
  of: PRIMARY_STORY
};
function argsHash(args) {
  return stringify(args);
}
var SourceContext = createContext({ sources: {} });
var UNKNOWN_ARGS_HASH = "--unknown--";
var SourceContainer = ({
  children,
  channel
}) => {
  const [sources, setSources] = useState({});
  useEffect(() => {
    const handleSnippetRendered = (idOrEvent, inputSource = null, inputFormat = false) => {
      const {
        id,
        args = void 0,
        source,
        format: format2
      } = typeof idOrEvent === "string" ? {
        id: idOrEvent,
        source: inputSource,
        format: inputFormat
      } : idOrEvent;
      const hash = args ? argsHash(args) : UNKNOWN_ARGS_HASH;
      setSources((current) => {
        const newSources = {
          ...current,
          [id]: {
            ...current[id],
            [hash]: { code: source, format: format2 }
          }
        };
        return newSources;
      });
    };
    channel.on(SNIPPET_RENDERED, handleSnippetRendered);
    return () => channel.off(SNIPPET_RENDERED, handleSnippetRendered);
  }, []);
  return /* @__PURE__ */ React17.createElement(SourceContext.Provider, { value: { sources } }, children);
};
var SourceState = /* @__PURE__ */ ((SourceState2) => {
  SourceState2["OPEN"] = "open";
  SourceState2["CLOSED"] = "closed";
  SourceState2["NONE"] = "none";
  return SourceState2;
})(SourceState || {});
var getSourceState = (stories) => {
  const states = stories.map((story) => story.parameters.docs?.source?.state).filter(Boolean);
  if (states.length === 0)
    return "closed" /* CLOSED */;
  return states[0];
};
var getStorySource = (storyId, args, sourceContext) => {
  const { sources } = sourceContext;
  const sourceMap = sources?.[storyId];
  const source = sourceMap?.[argsHash(args)] || sourceMap?.[UNKNOWN_ARGS_HASH];
  return source || { code: "" };
};
var getSnippet = ({
  snippet,
  storyContext,
  typeFromProps,
  transformFromProps
}) => {
  const { __isArgsStory: isArgsStory } = storyContext.parameters;
  const sourceParameters = storyContext.parameters.docs?.source || {};
  const type = typeFromProps || sourceParameters.type || SourceType.AUTO;
  if (sourceParameters.code !== void 0) {
    return sourceParameters.code;
  }
  const useSnippet = (
    // if user has explicitly set this as dynamic, use snippet
    type === SourceType.DYNAMIC || // if this is an args story and there's a snippet
    type === SourceType.AUTO && snippet && isArgsStory
  );
  const code = useSnippet ? snippet : sourceParameters.originalSource || "";
  if (sourceParameters.transformSource) {
    deprecate(dedent2`The \`transformSource\` parameter at \`parameters.docs.source.transformSource\` is deprecated, please use \`parameters.docs.source.transform\` instead. 
    
    Please refer to the migration guide: https://github.com/storybookjs/storybook/blob/next/MIGRATION.md#source-block
  `);
  }
  if (storyContext.parameters.docs?.transformSource) {
    deprecate(dedent2`The \`transformSource\` parameter at \`parameters.docs.transformSource\` is deprecated, please use \`parameters.docs.source.transform\` instead. 
    
    Please refer to the migration guide: https://github.com/storybookjs/storybook/blob/next/MIGRATION.md#source-block
  `);
  }
  if (storyContext.parameters.jsx?.transformSource) {
    deprecate(dedent2`The \`transformSource\` parameter at \`parameters.jsx.transformSource\` is deprecated, please use \`parameters.docs.source.transform\` instead. 
    
    Please refer to the migration guide: https://github.com/storybookjs/storybook/blob/next/MIGRATION.md#source-block
  `);
  }
  const transformer = transformFromProps ?? sourceParameters.transform ?? sourceParameters.transformSource ?? // deprecated
  storyContext.parameters.docs?.transformSource ?? // deprecated
  storyContext.parameters.jsx?.transformSource;
  return transformer?.(code, storyContext) || code;
};
var useSourceProps = (props, docsContext, sourceContext) => {
  const storyIds = props.ids || (props.id ? [props.id] : []);
  const storiesFromIds = useStories(storyIds, docsContext);
  let stories = storiesFromIds;
  const { of } = props;
  if ("of" in props && of === void 0) {
    throw new Error("Unexpected `of={undefined}`, did you mistype a CSF file reference?");
  }
  if (of) {
    const resolved = docsContext.resolveOf(of, ["story"]);
    stories = [resolved.story];
  } else if (stories.length === 0) {
    try {
      stories = [docsContext.storyById()];
    } catch (err) {
    }
  }
  if (!storiesFromIds.every(Boolean)) {
    return { error: "Oh no! The source is not available." /* SOURCE_UNAVAILABLE */, state: "none" /* NONE */ };
  }
  const sourceParameters = stories[0]?.parameters?.docs?.source || {};
  let { code } = props;
  let format2 = props.format ?? sourceParameters.format;
  const language = props.language ?? sourceParameters.language ?? "jsx";
  const dark = props.dark ?? sourceParameters.dark ?? false;
  if (!code) {
    code = stories.map((story, index) => {
      if (!story)
        return "";
      const storyContext = docsContext.getStoryContext(story);
      const argsForSource = props.__forceInitialArgs ? storyContext.initialArgs : storyContext.unmappedArgs;
      const source = getStorySource(story.id, argsForSource, sourceContext);
      if (index === 0) {
        format2 = source.format ?? story.parameters.docs?.source?.format ?? false;
      }
      return getSnippet({
        snippet: source.code,
        storyContext: { ...storyContext, args: argsForSource },
        typeFromProps: props.type,
        transformFromProps: props.transform
      });
    }).join("\n\n");
  }
  const state = getSourceState(stories);
  return code ? {
    code,
    format: format2,
    language,
    dark,
    // state is used by the Canvas block when it calls this function
    state
  } : { error: "Oh no! The source is not available." /* SOURCE_UNAVAILABLE */, state };
};
var Source2 = (props) => {
  if (props.id) {
    deprecate(dedent2`The \`id\` prop on Source is deprecated, please use the \`of\` prop instead to reference a story. 
    
    Please refer to the migration guide: https://github.com/storybookjs/storybook/blob/next/MIGRATION.md#source-block
  `);
  }
  if (props.ids) {
    deprecate(dedent2`The \`ids\` prop on Source is deprecated, please use the \`of\` prop instead to reference a story. 
    
    Please refer to the migration guide: https://github.com/storybookjs/storybook/blob/next/MIGRATION.md#source-block
  `);
  }
  const sourceContext = useContext(SourceContext);
  const docsContext = useContext(DocsContext);
  const { state, ...sourceProps } = useSourceProps(props, docsContext, sourceContext);
  return /* @__PURE__ */ React17.createElement(Source, { ...sourceProps });
};
var getStoryId2 = (props, context) => {
  const { id, of, meta, story } = props;
  if ("of" in props && of === void 0) {
    throw new Error("Unexpected `of={undefined}`, did you mistype a CSF file reference?");
  }
  if (id) {
    deprecate(dedent2`Referencing stories by \`id\` is deprecated, please use \`of\` instead. 
    
      Please refer to the migration guide: https://github.com/storybookjs/storybook/blob/next/MIGRATION.md#story-block'`);
    return id;
  }
  const { name } = props;
  if (name) {
    deprecate(dedent2`Referencing stories by \`name\` is deprecated, please use \`of\` instead. 
    
      Please refer to the migration guide: https://github.com/storybookjs/storybook/blob/next/MIGRATION.md#story-block'`);
    return context.storyIdByName(name);
  }
  if (story) {
    deprecate(dedent2`The \`story\` prop is deprecated, please export your stories from CSF files and reference them with \`of={}\`.

      Please refer to the migration guide: https://github.com/storybookjs/storybook/blob/next/MIGRATION.md#story-block'`);
  }
  if (meta)
    context.referenceMeta(meta, false);
  const resolved = context.resolveOf(of || story || "story", ["story"]);
  return resolved.story.id;
};
var getStoryProps = (props, story, context) => {
  const { parameters = {} } = story || {};
  const { docs = {} } = parameters;
  const storyParameters = docs.story || {};
  if (docs.disable) {
    return null;
  }
  const { inlineStories, iframeHeight } = docs;
  if (typeof inlineStories !== "undefined")
    deprecate(dedent2`The \`docs.inlineStories\` parameter is deprecated, use \`docs.story.inline\` instead. 
    
      Please refer to the migration guide: https://github.com/storybookjs/storybook/blob/next/MIGRATION.md#autodocs-changes'
    `);
  const inline = props.inline ?? storyParameters.inline ?? inlineStories ?? false;
  if (typeof iframeHeight !== "undefined") {
    deprecate(dedent2`The \`docs.iframeHeight\` parameter is deprecated, use \`docs.story.iframeHeight\` instead. 
    
      Please refer to the migration guide: https://github.com/storybookjs/storybook/blob/next/MIGRATION.md#autodocs-changes'
    `);
  }
  if (inline) {
    const height2 = props.height ?? storyParameters.height;
    const autoplay = props.autoplay ?? storyParameters.autoplay ?? false;
    return {
      story,
      inline: true,
      height: height2,
      autoplay,
      // eslint-disable-next-line no-underscore-dangle
      forceInitialArgs: !!props.__forceInitialArgs,
      // eslint-disable-next-line no-underscore-dangle
      primary: !!props.__primary,
      renderStoryToElement: context.renderStoryToElement
    };
  }
  const height = props.height ?? storyParameters.height ?? storyParameters.iframeHeight ?? iframeHeight ?? "100px";
  return {
    story,
    inline: false,
    height,
    // eslint-disable-next-line no-underscore-dangle
    primary: !!props.__primary
  };
};
var Story2 = (props = { __forceInitialArgs: false, __primary: false }) => {
  const context = useContext(DocsContext);
  const storyId = getStoryId2(props, context);
  const story = useStory(storyId, context);
  if (!story) {
    return /* @__PURE__ */ React17.createElement(StorySkeleton, null);
  }
  const storyProps = getStoryProps(props, story, context);
  if (!storyProps) {
    return null;
  }
  return /* @__PURE__ */ React17.createElement(Story, { ...storyProps });
};

// src/blocks/Canvas.tsx
var useDeprecatedPreviewProps = ({
  withSource,
  mdxSource,
  children,
  layout: layoutProp,
  ...props
}, docsContext, sourceContext) => {
  const storyIds = Children.toArray(children).filter((c) => c.props && (c.props.id || c.props.name || c.props.of)).map((c) => getStoryId2(c.props, docsContext));
  const stories = useStories(storyIds, docsContext);
  const isLoading = stories.some((s) => !s);
  const sourceProps = useSourceProps(
    {
      ...mdxSource ? { code: decodeURI(mdxSource) } : { ids: storyIds },
      ...props.of && { of: props.of }
    },
    docsContext,
    sourceContext
  );
  if (withSource === "none" /* NONE */) {
    return { isLoading, previewProps: props };
  }
  let layout = layoutProp;
  Children.forEach(children, (child) => {
    if (layout) {
      return;
    }
    layout = child?.props?.parameters?.layout;
  });
  stories.forEach((story) => {
    if (layout || !story) {
      return;
    }
    layout = story?.parameters.layout ?? story.parameters.docs?.canvas?.layout;
  });
  return {
    isLoading,
    previewProps: {
      ...props,
      // pass through columns etc.
      layout: layout ?? "padded",
      withSource: sourceProps,
      isExpanded: (withSource || sourceProps.state) === "open" /* OPEN */
    }
  };
};
var Canvas = (props) => {
  const docsContext = useContext(DocsContext);
  const sourceContext = useContext(SourceContext);
  const { children, of, source } = props;
  if ("of" in props && of === void 0) {
    throw new Error("Unexpected `of={undefined}`, did you mistype a CSF file reference?");
  }
  const { isLoading, previewProps } = useDeprecatedPreviewProps(props, docsContext, sourceContext);
  let story;
  let sourceProps;
  let hookError;
  try {
    ({ story } = useOf(of || "story", ["story"]));
  } catch (error) {
    if (!children) {
      hookError = error;
    }
  }
  try {
    sourceProps = useSourceProps({ ...source, ...of && { of } }, docsContext, sourceContext);
  } catch (error) {
    if (!children) {
      hookError = error;
    }
  }
  if (hookError) {
    throw hookError;
  }
  if (props.withSource) {
    deprecate(dedent2`Setting source state with \`withSource\` is deprecated, please use \`sourceState\` with 'hidden', 'shown' or 'none' instead. 
    
    Please refer to the migration guide: https://github.com/storybookjs/storybook/blob/next/MIGRATION.md#canvas-block
    `);
  }
  if (props.mdxSource) {
    deprecate(dedent2`Setting source code with \`mdxSource\` is deprecated, please use source={{code: '...'}} instead. 
    
    Please refer to the migration guide: https://github.com/storybookjs/storybook/blob/next/MIGRATION.md#canvas-block
    `);
  }
  if (props.isColumn !== void 0 || props.columns !== void 0) {
    deprecate(dedent2`\`isColumn\` and \`columns\` props are deprecated as the Canvas block now only supports showing a single story. 
    
    Please refer to the migration guide: https://github.com/storybookjs/storybook/blob/next/MIGRATION.md#canvas-block
    `);
  }
  if (children) {
    deprecate(dedent2`Passing children to Canvas is deprecated, please use the \`of\` prop instead to reference a story. 
    
    Please refer to the migration guide: https://github.com/storybookjs/storybook/blob/next/MIGRATION.md#canvas-block
  `);
    return isLoading ? /* @__PURE__ */ React17.createElement(PreviewSkeleton, null) : /* @__PURE__ */ React17.createElement(Preview, { ...previewProps }, children);
  }
  const layout = props.layout ?? story.parameters.layout ?? story.parameters.docs?.canvas?.layout ?? "padded";
  const withToolbar = props.withToolbar ?? story.parameters.docs?.canvas?.withToolbar ?? false;
  const additionalActions = props.additionalActions ?? story.parameters.docs?.canvas?.additionalActions;
  const sourceState = props.sourceState ?? story.parameters.docs?.canvas?.sourceState ?? "hidden";
  const className = props.className ?? story.parameters.docs?.canvas?.className;
  return /* @__PURE__ */ React17.createElement(
    Preview,
    {
      withSource: sourceState === "none" ? void 0 : sourceProps,
      isExpanded: sourceState === "shown",
      withToolbar,
      additionalActions,
      className,
      layout
    },
    /* @__PURE__ */ React17.createElement(Story2, { of: of || story.moduleExport, meta: props.meta, ...props.story })
  );
};
var useGlobals2 = (story, context) => {
  const storyContext = context.getStoryContext(story);
  const [globals, setGlobals] = useState(storyContext.globals);
  useEffect(() => {
    const onGlobalsUpdated = (changed) => {
      setGlobals(changed.globals);
    };
    context.channel.on(GLOBALS_UPDATED, onGlobalsUpdated);
    return () => context.channel.off(GLOBALS_UPDATED, onGlobalsUpdated);
  }, [context.channel]);
  return [globals];
};
var useArgs2 = (story, context) => {
  const result = useArgsIfDefined(story, context);
  if (!result)
    throw new Error("No result when story was defined");
  return result;
};
var useArgsIfDefined = (story, context) => {
  const storyContext = story ? context.getStoryContext(story) : { args: {} };
  const { id: storyId } = story || { id: "none" };
  const [args, setArgs] = useState(storyContext.args);
  useEffect(() => {
    const onArgsUpdated = (changed) => {
      if (changed.storyId === storyId) {
        setArgs(changed.args);
      }
    };
    context.channel.on(STORY_ARGS_UPDATED, onArgsUpdated);
    return () => context.channel.off(STORY_ARGS_UPDATED, onArgsUpdated);
  }, [storyId, context.channel]);
  const updateArgs = useCallback(
    (updatedArgs) => context.channel.emit(UPDATE_STORY_ARGS, { storyId, updatedArgs }),
    [storyId, context.channel]
  );
  const resetArgs = useCallback(
    (argNames) => context.channel.emit(RESET_STORY_ARGS, { storyId, argNames }),
    [storyId, context.channel]
  );
  return story && [args, updateArgs, resetArgs];
};

// src/blocks/Controls.tsx
var Controls3 = (props) => {
  const { of } = props;
  if ("of" in props && of === void 0) {
    throw new Error("Unexpected `of={undefined}`, did you mistype a CSF file reference?");
  }
  const context = useContext(DocsContext);
  const { story } = context.resolveOf(of || "story", ["story"]);
  const { parameters, argTypes } = story;
  const controlsParameters = parameters.docs?.controls || {};
  const include = props.include ?? controlsParameters.include;
  const exclude = props.exclude ?? controlsParameters.exclude;
  const sort = props.sort ?? controlsParameters.sort;
  const [args, updateArgs, resetArgs] = useArgs2(story, context);
  const [globals] = useGlobals2(story, context);
  const filteredArgTypes = filterArgTypes(argTypes, include, exclude);
  return /* @__PURE__ */ React17.createElement(
    ArgsTable,
    {
      rows: filteredArgTypes,
      args,
      globals,
      updateArgs,
      resetArgs,
      sort
    }
  );
};
var { document: document2 } = global;
var assertIsFn = (val) => {
  if (typeof val !== "function") {
    throw new Error(`Expected story function, got: ${val}`);
  }
  return val;
};
var AddContext = (props) => {
  const { children, ...rest } = props;
  const parentContext = React17.useContext(DocsContext);
  return /* @__PURE__ */ React17.createElement(DocsContext.Provider, { value: { ...parentContext, ...rest } }, children);
};
var CodeOrSourceMdx = ({ className, children, ...rest }) => {
  if (typeof className !== "string" && (typeof children !== "string" || !children.match(/[\n\r]/g))) {
    return /* @__PURE__ */ React17.createElement(Code, null, children);
  }
  const language = className && className.split("-");
  return /* @__PURE__ */ React17.createElement(
    Source,
    {
      language: language && language[1] || "plaintext",
      format: false,
      code: children,
      ...rest
    }
  );
};
function navigate(context, url) {
  context.channel.emit(NAVIGATE_URL, url);
}
var A = components.a;
var AnchorInPage = ({ hash, children }) => {
  const context = useContext(DocsContext);
  return /* @__PURE__ */ React17.createElement(
    A,
    {
      href: hash,
      target: "_self",
      onClick: (event) => {
        const id = hash.substring(1);
        const element = document2.getElementById(id);
        if (element) {
          navigate(context, hash);
        }
      }
    },
    children
  );
};
var AnchorMdx = (props) => {
  const { href, target, children, ...rest } = props;
  const context = useContext(DocsContext);
  if (href) {
    if (href.startsWith("#")) {
      return /* @__PURE__ */ React17.createElement(AnchorInPage, { hash: href }, children);
    }
    if (target !== "_blank" && !href.startsWith("https://")) {
      return /* @__PURE__ */ React17.createElement(
        A,
        {
          href,
          onClick: (event) => {
            const LEFT_BUTTON = 0;
            const isLeftClick = event.button === LEFT_BUTTON && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey;
            if (isLeftClick) {
              event.preventDefault();
              navigate(context, event.currentTarget.getAttribute("href"));
            }
          },
          target,
          ...rest
        },
        children
      );
    }
  }
  return /* @__PURE__ */ React17.createElement(A, { ...props });
};
var SUPPORTED_MDX_HEADERS = ["h1", "h2", "h3", "h4", "h5", "h6"];
var OcticonHeaders = SUPPORTED_MDX_HEADERS.reduce(
  (acc, headerType) => ({
    ...acc,
    [headerType]: styled(headerType)({
      "& svg": {
        position: "relative",
        top: "-0.1em",
        visibility: "hidden"
      },
      "&:hover svg": {
        visibility: "visible"
      }
    })
  }),
  {}
);
var OcticonAnchor = styled.a(() => ({
  float: "left",
  lineHeight: "inherit",
  paddingRight: "10px",
  marginLeft: "-24px",
  // Allow the theme's text color to override the default link color.
  color: "inherit"
}));
var HeaderWithOcticonAnchor = ({
  as,
  id,
  children,
  ...rest
}) => {
  const context = useContext(DocsContext);
  const OcticonHeader = OcticonHeaders[as];
  const hash = `#${id}`;
  return /* @__PURE__ */ React17.createElement(OcticonHeader, { id, ...rest }, /* @__PURE__ */ React17.createElement(
    OcticonAnchor,
    {
      "aria-hidden": "true",
      href: hash,
      tabIndex: -1,
      target: "_self",
      onClick: (event) => {
        const element = document2.getElementById(id);
        if (element) {
          navigate(context, hash);
        }
      }
    },
    /* @__PURE__ */ React17.createElement(Icon.Link, null)
  ), children);
};
var HeaderMdx = (props) => {
  const { as, id, children, ...rest } = props;
  if (id) {
    return /* @__PURE__ */ React17.createElement(HeaderWithOcticonAnchor, { as, id, ...rest }, children);
  }
  const Component4 = as;
  const { as: omittedAs, ...withoutAs } = props;
  return /* @__PURE__ */ React17.createElement(Component4, { ...nameSpaceClassNames(withoutAs, as) });
};
var HeadersMdx = SUPPORTED_MDX_HEADERS.reduce(
  (acc, headerType) => ({
    ...acc,
    // @ts-expect-error (Converted from ts-ignore)
    [headerType]: (props) => /* @__PURE__ */ React17.createElement(HeaderMdx, { as: headerType, ...props })
  }),
  {}
);

// src/blocks/Markdown.tsx
var Markdown2 = (props) => {
  if (!props.children) {
    return null;
  }
  if (typeof props.children !== "string") {
    throw new Error(
      dedent2`The Markdown block only accepts children as a single string, but children were of type: '${typeof props.children}'
        This is often caused by not wrapping the child in a template string.
        
        This is invalid:
        <Markdown>
          # Some heading
          A paragraph
        </Markdown>

        Instead do:
        <Markdown>
        {\`
          # Some heading
          A paragraph
        \`}
        </Markdown>
      `
    );
  }
  return /* @__PURE__ */ React17.createElement(
    Markdown,
    {
      ...props,
      options: {
        forceBlock: true,
        overrides: {
          code: CodeOrSourceMdx,
          a: AnchorMdx,
          ...HeadersMdx,
          ...props?.options?.overrides
        },
        ...props?.options
      }
    }
  );
};

// src/blocks/Description.tsx
var DescriptionType = /* @__PURE__ */ ((DescriptionType2) => {
  DescriptionType2["INFO"] = "info";
  DescriptionType2["NOTES"] = "notes";
  DescriptionType2["DOCGEN"] = "docgen";
  DescriptionType2["AUTO"] = "auto";
  return DescriptionType2;
})(DescriptionType || {});
var DEPRECATION_MIGRATION_LINK = "https://github.com/storybookjs/storybook/blob/next/MIGRATION.md#description-block-parametersnotes-and-parametersinfo";
var getNotes = (notes) => notes && (typeof notes === "string" ? notes : str(notes.markdown) || str(notes.text));
var getInfo = (info) => info && (typeof info === "string" ? info : str(info.text));
var noDescription = (component) => null;
var getDescriptionFromResolvedOf = (resolvedOf) => {
  switch (resolvedOf.type) {
    case "story": {
      return resolvedOf.story.parameters.docs?.description?.story || null;
    }
    case "meta": {
      const { parameters, component } = resolvedOf.preparedMeta;
      const metaDescription = parameters.docs?.description?.component;
      if (metaDescription) {
        return metaDescription;
      }
      return parameters.docs?.extractComponentDescription?.(component, {
        component,
        parameters
      }) || null;
    }
    case "component": {
      const {
        component,
        projectAnnotations: { parameters }
      } = resolvedOf;
      return parameters.docs?.extractComponentDescription?.(component, {
        component,
        parameters
      }) || null;
    }
    default: {
      throw new Error(
        `Unrecognized module type resolved from 'useOf', got: ${resolvedOf.type}`
      );
    }
  }
};
var getDescriptionFromDeprecatedProps = ({ type, markdown, children }, { storyById }) => {
  const { component, parameters } = storyById();
  if (children || markdown) {
    return children || markdown;
  }
  const { notes, info, docs } = parameters;
  if (Boolean(notes) || Boolean(info)) {
    deprecate(
      `Using 'parameters.notes' or 'parameters.info' properties to describe stories is deprecated. See ${DEPRECATION_MIGRATION_LINK}`
    );
  }
  const { extractComponentDescription = noDescription, description } = docs || {};
  const componentDescriptionParameter = description?.component;
  if (componentDescriptionParameter) {
    return componentDescriptionParameter;
  }
  switch (type) {
    case "info" /* INFO */:
      return getInfo(info);
    case "notes" /* NOTES */:
      return getNotes(notes);
    case "docgen" /* DOCGEN */:
    case "auto" /* AUTO */:
    default:
      return extractComponentDescription(component, { component, ...parameters });
  }
};
var DescriptionContainer = (props) => {
  const { of, type, markdown: markdownProp, children } = props;
  if ("of" in props && of === void 0) {
    throw new Error("Unexpected `of={undefined}`, did you mistype a CSF file reference?");
  }
  const context = useContext(DocsContext);
  const resolvedOf = useOf(of || "meta");
  let markdown;
  if (type || markdownProp || children) {
    markdown = getDescriptionFromDeprecatedProps(props, context);
  } else {
    markdown = getDescriptionFromResolvedOf(resolvedOf);
  }
  if (type) {
    deprecate(
      `Manually specifying description type is deprecated. See ${DEPRECATION_MIGRATION_LINK}`
    );
  }
  if (markdownProp) {
    deprecate(
      `The 'markdown' prop on the Description block is deprecated. See ${DEPRECATION_MIGRATION_LINK}`
    );
  }
  if (children) {
    deprecate(
      `The 'children' prop on the Description block is deprecated. See ${DEPRECATION_MIGRATION_LINK}`
    );
  }
  return markdown ? /* @__PURE__ */ React17.createElement(Markdown2, null, markdown) : null;
};
var Wrapper9 = styled.div(({ theme }) => ({
  width: "10rem",
  "@media (max-width: 768px)": {
    display: "none"
  }
}));
var Content2 = styled.div(({ theme }) => ({
  position: "fixed",
  top: 0,
  width: "10rem",
  paddingTop: "4rem",
  fontFamily: theme.typography.fonts.base,
  fontSize: theme.typography.size.s2,
  WebkitFontSmoothing: "antialiased",
  MozOsxFontSmoothing: "grayscale",
  WebkitTapHighlightColor: "rgba(0, 0, 0, 0)",
  WebkitOverflowScrolling: "touch",
  "& *": {
    boxSizing: "border-box"
  },
  "& > .toc-wrapper > .toc-list": {
    paddingLeft: 0,
    borderLeft: `solid 2px ${theme.color.mediumlight}`,
    ".toc-list": {
      paddingLeft: 0,
      borderLeft: `solid 2px ${theme.color.mediumlight}`,
      ".toc-list": {
        paddingLeft: 0,
        borderLeft: `solid 2px ${theme.color.mediumlight}`
      }
    }
  },
  "& .toc-list-item": {
    position: "relative",
    listStyleType: "none",
    marginLeft: 20,
    paddingTop: 3,
    paddingBottom: 3
  },
  "& .toc-list-item::before": {
    content: '""',
    position: "absolute",
    height: "100%",
    top: 0,
    left: 0,
    transform: `translateX(calc(-2px - 20px))`,
    borderLeft: `solid 2px ${theme.color.mediumdark}`,
    opacity: 0,
    transition: "opacity 0.2s"
  },
  "& .toc-list-item.is-active-li::before": {
    opacity: 1
  },
  "& .toc-list-item > a": {
    color: theme.color.defaultText,
    textDecoration: "none"
  },
  "& .toc-list-item.is-active-li > a": {
    fontWeight: 600,
    color: theme.color.secondary,
    textDecoration: "none"
  }
}));
var Heading = styled.p(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.875em",
  color: theme.textColor,
  textTransform: "uppercase",
  marginBottom: 10
}));
var OptionalTitle = ({ title }) => {
  if (title === null) {
    return null;
  }
  if (typeof title === "string") {
    return /* @__PURE__ */ React17.createElement(Heading, null, title);
  }
  return title;
};
var TableOfContents = ({
  title,
  disable,
  headingSelector,
  contentsSelector,
  ignoreSelector,
  unsafeTocbotOptions
}) => {
  useEffect(() => {
    const configuration = {
      tocSelector: ".toc-wrapper",
      contentSelector: contentsSelector ?? ".sbdocs-content",
      headingSelector: headingSelector ?? "h3",
      ignoreSelector: ignoreSelector ?? ".skip-toc",
      headingsOffset: 40,
      scrollSmoothOffset: -40,
      /**
       * Ignore headings that did not
       * come from the main markdown code.
       */
      // ignoreSelector: ':not(.sbdocs), .hide-from-toc',
      orderedList: false,
      /**
       * Prevent default linking behavior,
       * leaving only the smooth scrolling.
       */
      onClick: () => false,
      ...unsafeTocbotOptions
    };
    const timeout = setTimeout(() => tocbot.init(configuration), 100);
    return () => {
      clearTimeout(timeout);
      tocbot.destroy();
    };
  }, [disable]);
  return /* @__PURE__ */ React17.createElement(React17.Fragment, null, /* @__PURE__ */ React17.createElement(Wrapper9, null, !disable ? /* @__PURE__ */ React17.createElement(Content2, null, /* @__PURE__ */ React17.createElement(OptionalTitle, { title: title || null }), /* @__PURE__ */ React17.createElement("div", { className: "toc-wrapper" })) : null));
};

// src/blocks/DocsContainer.tsx
var { document: document3, window: globalWindow3 } = global;
var DocsContainer = ({
  context,
  theme,
  children
}) => {
  let toc;
  try {
    const meta = context.resolveOf("meta", ["meta"]);
    toc = meta.preparedMeta.parameters?.docs?.toc;
  } catch (err) {
    toc = context?.projectAnnotations?.parameters?.docs?.toc;
  }
  useEffect(() => {
    let url;
    try {
      url = new URL(globalWindow3.parent.location.toString());
      if (url.hash) {
        const element = document3.getElementById(url.hash.substring(1));
        if (element) {
          setTimeout(() => {
            scrollToElement(element);
          }, 200);
        }
      }
    } catch (err) {
    }
  });
  return /* @__PURE__ */ React17.createElement(DocsContext.Provider, { value: context }, /* @__PURE__ */ React17.createElement(SourceContainer, { channel: context.channel }, /* @__PURE__ */ React17.createElement(ThemeProvider, { theme: ensure(theme) }, /* @__PURE__ */ React17.createElement(
    DocsPageWrapper,
    {
      toc: toc ? /* @__PURE__ */ React17.createElement(TableOfContents, { className: "sbdocs sbdocs-toc--custom", ...toc }) : null
    },
    children
  ))));
};
var STORY_KIND_PATH_SEPARATOR = /\s*\/\s*/;
var extractTitle = (title) => {
  const groups = title.trim().split(STORY_KIND_PATH_SEPARATOR);
  return groups && groups[groups.length - 1] || title;
};
var Title3 = ({ children }) => {
  const context = useContext(DocsContext);
  const content = children || extractTitle(context.storyById().title);
  return content ? /* @__PURE__ */ React17.createElement(Title, { className: "sbdocs-title sb-unstyled" }, content) : null;
};
var Subtitle2 = ({ children }) => {
  const docsContext = useContext(DocsContext);
  const { parameters } = docsContext.storyById();
  const content = children || parameters?.componentSubtitle;
  return content ? /* @__PURE__ */ React17.createElement(Subtitle, { className: "sbdocs-subtitle sb-unstyled" }, content) : null;
};
var Subheading = ({ children, disableAnchor }) => {
  if (disableAnchor || typeof children !== "string") {
    return /* @__PURE__ */ React17.createElement(H3, null, children);
  }
  const tagID = children.toLowerCase().replace(/[^a-z0-9]/gi, "-");
  return /* @__PURE__ */ React17.createElement(HeaderMdx, { as: "h3", id: tagID }, children);
};

// src/blocks/DocsStory.tsx
var DocsStory = ({
  of,
  expanded = true,
  withToolbar: withToolbarProp = false,
  __forceInitialArgs = false,
  __primary = false
}) => {
  const { story } = useOf(of || "story", ["story"]);
  const withToolbar = story.parameters.docs?.canvas?.withToolbar ?? withToolbarProp;
  return /* @__PURE__ */ React17.createElement(Anchor, { storyId: story.id }, expanded && /* @__PURE__ */ React17.createElement(React17.Fragment, null, /* @__PURE__ */ React17.createElement(Subheading, null, story.name), /* @__PURE__ */ React17.createElement(DescriptionContainer, { of })), /* @__PURE__ */ React17.createElement(
    Canvas,
    {
      of,
      withToolbar,
      story: { __forceInitialArgs, __primary },
      source: { __forceInitialArgs }
    }
  ));
};

// src/blocks/Primary.tsx
var Primary = ({ name }) => {
  const docsContext = useContext(DocsContext);
  if (name) {
    deprecate(dedent2`\`name\` prop is deprecated on the Primary block.
    The Primary block should only be used to render the primary story, which is automatically found.
    `);
  }
  const storyId = name && docsContext.storyIdByName(name);
  const story = docsContext.storyById(storyId);
  return story ? /* @__PURE__ */ React17.createElement(DocsStory, { of: story.moduleExport, expanded: false, __primary: true, withToolbar: true }) : null;
};
var Heading2 = ({
  children,
  disableAnchor,
  ...props
}) => {
  if (disableAnchor || typeof children !== "string") {
    return /* @__PURE__ */ React17.createElement(H2, null, children);
  }
  const tagID = children.toLowerCase().replace(/[^a-z0-9]/gi, "-");
  return /* @__PURE__ */ React17.createElement(HeaderMdx, { as: "h2", id: tagID, ...props }, children);
};

// src/blocks/Stories.tsx
var StyledHeading = styled(Heading2)(({ theme }) => ({
  fontSize: `${theme.typography.size.s2 - 1}px`,
  fontWeight: theme.typography.weight.bold,
  lineHeight: "16px",
  letterSpacing: "0.35em",
  textTransform: "uppercase",
  color: theme.textMutedColor,
  border: 0,
  marginBottom: "12px",
  "&:first-of-type": {
    // specificity issue
    marginTop: "56px"
  }
}));
var Stories = ({ title, includePrimary = true }) => {
  const { componentStories } = useContext(DocsContext);
  let stories = componentStories().filter((story) => !story.parameters?.docs?.disable);
  if (!includePrimary)
    stories = stories.slice(1);
  if (!stories || stories.length === 0) {
    return null;
  }
  return /* @__PURE__ */ React17.createElement(React17.Fragment, null, /* @__PURE__ */ React17.createElement(StyledHeading, null, title), stories.map(
    (story) => story && /* @__PURE__ */ React17.createElement(DocsStory, { key: story.id, of: story.moduleExport, expanded: true, __forceInitialArgs: true })
  ));
};
Stories.defaultProps = {
  title: "Stories"
};

// src/blocks/DocsPage.tsx
var DocsPage = () => {
  const resolvedOf = useOf("meta", ["meta"]);
  const { stories } = resolvedOf.csfFile;
  const isSingleStory = Object.keys(stories).length === 1;
  return /* @__PURE__ */ React17.createElement(React17.Fragment, null, /* @__PURE__ */ React17.createElement(Title3, null), /* @__PURE__ */ React17.createElement(Subtitle2, null), /* @__PURE__ */ React17.createElement(DescriptionContainer, { of: "meta" }), isSingleStory ? /* @__PURE__ */ React17.createElement(DescriptionContainer, { of: "story" }) : null, /* @__PURE__ */ React17.createElement(Primary, null), /* @__PURE__ */ React17.createElement(Controls3, null), isSingleStory ? null : /* @__PURE__ */ React17.createElement(Stories, null));
};

// src/blocks/Docs.tsx
function Docs({
  context,
  docsParameter
}) {
  const Container = docsParameter.container || DocsContainer;
  const Page = docsParameter.page || DocsPage;
  return /* @__PURE__ */ React17.createElement(Container, { context, theme: docsParameter.theme }, /* @__PURE__ */ React17.createElement(Page, null));
}
var ExternalDocsContext = class extends DocsContext$1 {
  constructor(channel, store, renderStoryToElement, processMetaExports) {
    super(channel, store, renderStoryToElement, []);
    this.channel = channel;
    this.store = store;
    this.renderStoryToElement = renderStoryToElement;
    this.processMetaExports = processMetaExports;
    this.referenceMeta = (metaExports, attach) => {
      const csfFile = this.processMetaExports(metaExports);
      this.referenceCSFFile(csfFile);
      super.referenceMeta(metaExports, attach);
    };
  }
};

// src/blocks/external/ExternalPreview.ts
var ConstantMap = class {
  constructor(prefix) {
    this.prefix = prefix;
    this.entries = /* @__PURE__ */ new Map();
  }
  get(key) {
    if (!this.entries.has(key)) {
      this.entries.set(key, `${this.prefix}${this.entries.size}`);
    }
    return this.entries.get(key);
  }
};
var ExternalPreview = class extends Preview$1 {
  constructor(projectAnnotations) {
    super(new Channel({}));
    this.projectAnnotations = projectAnnotations;
    this.importPaths = new ConstantMap("./importPath/");
    this.titles = new ConstantMap("title-");
    this.storyIndex = { v: 4, entries: {} };
    this.moduleExportsByImportPath = {};
    this.processMetaExports = (metaExports) => {
      const importPath = this.importPaths.get(metaExports);
      this.moduleExportsByImportPath[importPath] = metaExports;
      const title = metaExports.default.title || this.titles.get(metaExports);
      const csfFile = this.storyStore.processCSFFileWithCache(
        metaExports,
        importPath,
        title
      );
      Object.values(csfFile.stories).forEach(({ id, name }) => {
        this.storyIndex.entries[id] = {
          id,
          importPath,
          title,
          name,
          type: "story"
        };
      });
      this.onStoriesChanged({ storyIndex: this.storyIndex });
      return csfFile;
    };
    this.docsContext = () => {
      return new ExternalDocsContext(
        this.channel,
        this.storyStore,
        this.renderStoryToElement.bind(this),
        this.processMetaExports.bind(this)
      );
    };
    this.initialize({
      getStoryIndex: () => this.storyIndex,
      importFn: (path) => {
        return Promise.resolve(this.moduleExportsByImportPath[path]);
      },
      getProjectAnnotations: () => composeConfigs([
        { parameters: { docs: { story: { inline: true } } } },
        this.projectAnnotations
      ])
    });
  }
};

// src/blocks/external/ExternalDocs.tsx
function usePreview(projectAnnotations) {
  const previewRef = useRef();
  if (!previewRef.current)
    previewRef.current = new ExternalPreview(projectAnnotations);
  return previewRef.current;
}
function ExternalDocs({
  projectAnnotationsList,
  children
}) {
  const projectAnnotations = composeConfigs(projectAnnotationsList);
  const preview2 = usePreview(projectAnnotations);
  const docsParameter = {
    ...projectAnnotations.parameters?.docs,
    page: () => children
  };
  const TDocs = Docs;
  return /* @__PURE__ */ React17.createElement(TDocs, { docsParameter, context: preview2.docsContext() });
}
var preview;
var ExternalDocsContainer = ({
  projectAnnotations,
  children
}) => {
  if (!preview)
    preview = new ExternalPreview(projectAnnotations);
  return /* @__PURE__ */ React17.createElement(DocsContext.Provider, { value: preview.docsContext() }, /* @__PURE__ */ React17.createElement(ThemeProvider, { theme: ensure(themes.light) }, children));
};
var Meta = ({ of }) => {
  const context = useContext(DocsContext);
  if (of) {
    context.referenceMeta(of, true);
  }
  try {
    const primary = context.storyById();
    return /* @__PURE__ */ React17.createElement(Anchor, { storyId: primary.id });
  } catch (err) {
    return null;
  }
};
var Unstyled = (props) => /* @__PURE__ */ React17.createElement("div", { ...props, className: "sb-unstyled" });
var Wrapper10 = ({ children }) => /* @__PURE__ */ React17.createElement("div", { style: { fontFamily: "sans-serif" } }, children);

export { AddContext, Anchor, AnchorMdx, ArgTypes, ArgsTable2 as ArgsTable, BooleanControl, Canvas, CodeOrSourceMdx, ColorControl, ColorItem, ColorPalette, ComponentsTable, Controls3 as Controls, DateControl, DescriptionContainer as Description, DescriptionType, Docs, DocsContainer, DocsContext, DocsPage, DocsStory, ExternalDocs, ExternalDocsContainer, FilesControl, HeaderMdx, HeadersMdx, Heading2 as Heading, IconGallery, IconItem, Markdown2 as Markdown, Meta, NumberControl, ObjectControl, OptionsControl, PRIMARY_STORY, Primary, ArgsTable as PureArgsTable, RangeControl, Source2 as Source, SourceContainer, SourceContext, SourceState, Stories, Story2 as Story, StoryTable, Subheading, Subtitle2 as Subtitle, TextControl, Title3 as Title, Typeset, UNKNOWN_ARGS_HASH, Unstyled, Wrapper10 as Wrapper, anchorBlockIdFromId, argsHash, assertIsFn, extractComponentArgTypes2 as extractComponentArgTypes, extractTitle, format, formatDate, formatTime, getComponent, getStoryId2 as getStoryId, getStoryProps, parse2 as parse, parseDate, parseTime, useOf, useSourceProps };
